import { Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import {
  AddIndividual,
  Application,
  DigitalIdGetApplicationIndividualsFn,
  DigitalIdResponse,
  DigitalIdResult,
  DigitalIdResultReceived,
  getApplicationStatus,
  Individual,
  IndividualFormDialogResult,
  IndividualWithResult,
  KycStatusMessage,
  OriginatorBusiness,
  parseAddressComponentsToString,
  parseRawAddress,
  SignerRoleTypes,
  UpdateApplicationIndividualDigitalIdMappingInput,
  UpdateApplicationIndividualDigitalIdMappingPayload,
  DigitalIdUpdateIndividualKycStatusFn,
  DigitalIdSyncIndividualKycFromFrankieOneFn,
  UpdateIndividualData,
  User,
  VerifyOneApplicationIndividualPayload,
  isInternalUser,
  isCreditUser,
  isAdminOrCreditUser,
  isOperations,
  BusinessNumberSearchValue,
  IndividualAdditionalRole,
  isAnalyst,
  BoundingBoxData,
  SaveConfirmationAction,
  SaveConfirmationConsequence,
  SaveConfirmationDialogResult,
  ConfirmationDialogResult,
  SavePartySideEffectDecision,
  PlacesSearchResultFn,
  PlacesDetailsFn,
  EditKycStatusDialogResult,
  ResendSignedConsentDialogResult,
  DownloadDocumentFromAzureFn,
  KycVerificationError,
  FrankieOneWorkflowIssue,
  PrivacyConsent,
  privacyConsentDisplayStatus,
  hasConsentDispatchFailure,
  currentPrivacyConsent,
  isConsentSignatureStale,
  latestConsentStatusChange,
  SaveApplicationPartiesData,
  SaveApplicationPartiesSummary,
  SaveApplicationPartyIndividualInput,
  SaveApplicationPartyEntityInput,
  KybPersonRowState,
  KybPersonRowStateWithDetails,
  KybReconciliationSaveData,
  KeyPersonNewIndividualDetails,
  getIdTypes,
  kycChangedFieldEntries,
  kycIndividualChanges,
  isApplicationSubmittedBeforeKybGoLive,
  canSkipPrivacyConsentSideEffects,
  ExistingPersonRef,
  findDuplicatePerson,
  ComparableIdentity,
  CONSENT_REISSUE_GATING_STATUSES,
  countryOfCitizenshipByName,
  IndividualFormValue,
  parseKybRiskScore,
  kybEntityAsKeyPeopleResult,
  KybEntitySummary,
  KybEntityIndividualPartySummary,
  KybManualPerson,
  RemoveApplicationPartyInput,
  isPartyStructureLocked,
  isIncompleteOnArrivalSource,
  KYB_INDIVIDUAL_SOURCE,
  PORTAL_GUARANTOR_INDIVIDUAL_SOURCE,
  AzureStorageDocument,
  UploadedDocumentRef,
  IndividualGuarantor,
  KybPersonRoleSelection,
  KybAddedParties,
  KybIndividualTrusteeDialogResult,
  applicantOf,
  primaryEntityOf,
  individualSignerRole,
  roleFlagsFromApiRole,
  ApplicationIndividual,
  individualEntityRoles,
  pickIndividualMatch,
} from '@portal-workspace/grow-shared-library';
import { animate, state, style, transition, trigger } from "@angular/animations";
import { finalize, map, tap } from 'rxjs/operators';
import {
  ApplicationDialogService,
  apiIndividualDob,
  apiIndividualResidentialAddress,
  apiIndividualTitle,
  PortalHotToastService,
  setupUntilDestroy,
  getUser, BusinessSearchFn,
  BusinessNumberSearchFn, applicationToApplicantFirstName, applicationToApplicantLastName,
} from '@portal-workspace/grow-ui-library';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { HotToastService } from '@ngneat/hot-toast';
import { loadingFor } from '@ngneat/loadoff';
import _ from 'lodash';
import moment from 'moment';
import {v4} from 'uuid';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { TagBoxComponent } from '../message-box/tag-box.component';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgClass, NgStyle, DatePipe, JsonPipe, AsyncPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatDividerModule } from '@angular/material/divider';
import { DomSanitizer } from '@angular/platform-browser';
import { Buffer } from 'buffer';
import mime from 'mime';
import { SignerRolePipe } from '../../pipes/signer-role.pipe';
import { ImageWithBoundingBoxComponent } from '../image-component/image-with-bounding-box.component';
import { PrivacyConsentStatusChipComponent } from '../privacy-consent-status-component/privacy-consent-status-chip.component';
import { DigitalIdService } from '../../../../../../apps/portal2/src/app/service/digital-id.service';
import { AuthService } from 'apps/portal2/src/app/service/auth.service';
import { KycVerificationDetailsComponent } from '../kyc-verification-details-component/kyc-verification-details.component';
import {
  KybEntitySummaryComponent,
  KybApplicationReconciliationReview,
  KybReconciliationDraftRestore,
  failedKybReconciliationDrafts,
} from './kyb-entity-summary.component';
import { AddApplicationPartyDialogResult } from '../application-dialog-component/add-application-party.dialog';
import { MergeIndividualsDialogResult } from '../application-dialog-component/merge-individuals.dialog';
import { ApplicationService } from 'apps/portal2/src/app/service/application.service';
import { KybPersonDetailsRequired } from '../kyb-key-people-component/kyb-entity-section.component';
import { UpdateApplicationDocumentFn } from './application-details.module';
import { ListApplicationDocumentFn } from './document-details.component';
import { individualIdDocumentRefs, pxToRem } from '../component-utils';
import { joinPersonName } from './join-person-name';
import { MatDialog } from '@angular/material/dialog';
import {
  AddGuarantorDialog,
  AddGuarantorDialogData,
  AddGuarantorDialogResult,
} from './add-guarantor.dialog';
// The person fields live in a single nested <individual-form> control; signer role, additional
// roles and contact remain KYC-specific controls alongside it.
export type KycVerificationIndividualFormGroup = FormGroup<{
  individual: FormControl<IndividualFormValue>,
  signerRole: FormControl<SignerRoleTypes>,
  additionalRoles: FormArray<AdditionalRoleFormGroup>,
  contact: FormControl<string | null>,
}>;
export type KycVerificationIndividualsFormArray = FormArray<KycVerificationIndividualFormGroup>;
export type AdditionalRoleFormGroup = FormGroup<{
  business: FormControl<BusinessNumberSearchValue>,
  signerRole: FormControl<SignerRoleTypes>,
}>

type IndividualInfo = { formGroup: KycVerificationIndividualFormGroup, individual: IndividualWithResult };

/**
 * The parts of an unsaved provisional row that do not come from the captured details: no id, no
 * guid, no verification result yet, and the signer role expressed as the API shape's role flags.
 *
 * id and guid are absent rather than null. isProvisionalIndividual tests `!element.guid`, so a
 * placeholder would make a provisional row read as saved; the cast is what the legacy builders
 * already did for the same reason.
 */
const provisionalIdentity = (signerRole: SignerRoleTypes, isGuarantor: boolean) => ({
  id: undefined,
  guid: undefined,
  sfContactId: undefined,
  isApplicant: false,
  isPrimaryContact: false,
  isGuarantor,
  isDeleted: false,
  // One application-level primary role, so individualSignerRole reproduces the role passed in.
  roles: [{
    ...roleFlagsFromApiRole(null),
    entityId: null,
    isSigner: signerRole === 'Signer' || signerRole === 'GuarantorSigner',
    isPrimaryRole: true,
  }],
  property: { isOwner: false },
  source: KYB_INDIVIDUAL_SOURCE,
  result: { status: 'WAITING' },
} as unknown as IndividualWithResult);

/**
 * A ticked KYB stakeholder not yet saved: the details captured for them where a dialog asked for
 * any, the tick state, and a provisional IndividualWithResult so the individuals table can show
 * them before they exist on the server.
 */
type PendingNewIndividual = {
  stakeholderId: number;
  /**
   * Absent for a stakeholder ticked straight through without the dialog. The save then carries no
   * details and the server materialises the row from the KYB record, registry address included -
   * sending the blanks this screen holds instead would overwrite that address with nothing.
   */
  details?: KeyPersonNewIndividualDetails;
  isSelected: boolean;
  isGuarantor: boolean;
  isSigner: boolean;
  idDocumentProvided: boolean;
  provisional: IndividualWithResult;
  /** Form group backing the provisional row's expanded editor. */
  info: IndividualInfo;
};

export class KycVerificationIndividualsDataSource extends DataSource<IndividualWithResult> {
  subject = new BehaviorSubject<IndividualWithResult[]>([]);
  connect(collectionViewer: CollectionViewer): Observable<IndividualWithResult[]> {
    return this.subject.asObservable();
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    // no-op: this source outlives the table, which is destroyed while the loading spinner shows
  }

  update(_data: IndividualWithResult[]) {
    // only show those that are not deleted
    const data = _data.filter(i => {
      return i.isDeleted !== true;
    });
    this.subject.next(data);
  }
}



export type GetOriginatorByIdFn = (originatorBusinessId: number) => Observable<OriginatorBusiness | null>;
export type DigitalIdAddIndividualFn = (applicationId: number, data: AddIndividual) => Observable<ApplicationIndividual>;
export type DigitalIdVerifyOneApplicationIndividualFn = (applicationId: number, individualId: string) => Observable<VerifyOneApplicationIndividualPayload>;
export type DigitalIdSendVerificationSmsFn = (applicationId: number, individualId: string) => Observable<{ verificationUrl: string, status: string }>;
// export type DigitalIdGetApplicationIndividualsFn = (applicationId: number) => Observable<GetApplicationindividualsPayload>;
export type DigitalIdUpdateApplicationIndividualDigitalIdMappingFn = (applicationId: number, individualId: string, input: UpdateApplicationIndividualDigitalIdMappingInput) => Observable<UpdateApplicationIndividualDigitalIdMappingPayload>;
export type SaveApplicationPartiesFn = (data: SaveApplicationPartiesData) => Observable<SaveApplicationPartiesSummary>;

// KYC form-control keys whose change is consent-critical (mirrors CONSENT_FORM_CRITICAL_FIELDS on the
// prefill side: name -> fullName, dob -> dateOfBirth, residentialAddress -> currentResidentialAddress,
// email -> emailAddress, identification -> drivingLicenceNo/passportNo). Used only to decide whether
// to show the reissue consequence in the consolidated confirmation dialog; the backend re-validates
// authoritatively.
const CONSENT_CRITICAL_CONTROLS = ['firstName', 'middleName', 'lastName', 'dob', 'residentialAddress', 'email', 'hasAlias', 'aliasNames', 'countryOfCitizenship', 'identification'];

// KYC form-control keys whose change triggers digital ID re-verification on an already-verified
// individual (identity fields: name / DOB / address).
const REVERIFY_CRITICAL_CONTROLS = ['firstName', 'middleName', 'lastName', 'dob', 'residentialAddress'];

// Save-confirmation dialog action ids for the officer-decidable side effects. The dialog echoes back
// unticked ids, which saveAll translates into the corresponding save-payload decisions.
const REVERIFY_ACTION_SUFFIX = ':reverify';
const CONSENT_REISSUE_ACTION_SUFFIX = ':consentReissue';
const SELECTED_PARTY_CONSENT_ACTION_ID = 'keyPeople:issueConsent';
const GUARANTOR_CREDIT_FLOW_ACTION_ID = 'guarantorChange:runCreditFlow';
// The signatories the credit flow assesses, matching the server's guarantor set.
const CREDIT_FLOW_GUARANTOR_ROLES: SignerRoleTypes[] = ['Guarantor', 'GuarantorSigner', 'CorporateGuarantor'];
const reverifyActionId = (individualId: string) => `${individualId}${REVERIFY_ACTION_SUFFIX}`;
const consentReissueActionId = (individualId: string) => `${individualId}${CONSENT_REISSUE_ACTION_SUFFIX}`;

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'kyc-verification',
  templateUrl: './kyc-verification.component.html',
  styleUrls: ['./kyc-verification.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'block' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  imports: [
    FlexModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCardModule,
    MatTableModule,
    MatTooltipModule,
    MatMenuModule,
    SignerRolePipe,
    MessageBoxComponent,
    TagBoxComponent,
    NgClass,
    ExtendedModule,
    NgStyle,
    DatePipe,
    TruncatePipe,
    ImageWithBoundingBoxComponent,
    JsonPipe,
    AsyncPipe,
    PrivacyConsentStatusChipComponent,
    KycVerificationDetailsComponent,
    KybEntitySummaryComponent,
]
})
export class KycVerificationComponent implements OnInit {

  loader = loadingFor('allIndividualsDigitalIdCheck', 'singleIndividualDigitalIdCheck', 'deleteIndividual', 'sendVerificationSms', 'syncKycFromFrankieOne');

  dataSource = new KycVerificationIndividualsDataSource();
  columnsToDisplay = ['dir', 'firstName', 'dob', 'signerRole', 'riskScore', 'riskRating', 'status', 'consentStatus', 'edited', 'action'];

  expandedElement: IndividualWithResult | null = null; // row element (IndividualWithResult) which is expanded
  
  // Tracking collapsed state of verification result details (true means collapsed)
  collapsedVerificationResults = new Set<string>();

  @ViewChild(KybEntitySummaryComponent) kybEntitySummary?: KybEntitySummaryComponent;

  isInternalUser = isInternalUser;
  isAnalyst = isAnalyst;
  isCreditUser = isCreditUser;

  placesSearchResultFn!: PlacesSearchResultFn;
  placesDetailsFn!: PlacesDetailsFn;
  isBrokerDisclosed = false;

  subscriptions: Subscription[] = [];
  loggedInUser: User | null = getUser();

  @Input({ required: true }) getOriginatorByIdFn!: GetOriginatorByIdFn;
  @Input({ required: true }) businessSearchFn!: BusinessSearchFn;
  @Input({ required: true }) addIndividualFn!: DigitalIdAddIndividualFn;
  @Input({ required: true }) verifyOneApplicationIndividualFn!: DigitalIdVerifyOneApplicationIndividualFn;
  @Input({ required: true }) sendVerificationSmsFn!: DigitalIdSendVerificationSmsFn;
  @Input({ required: true }) getApplicationIndividualsFn!: DigitalIdGetApplicationIndividualsFn;
  @Input({ required: true }) updateApplicationIndividualDigitalIdMappingFn!: DigitalIdUpdateApplicationIndividualDigitalIdMappingFn;
  @Input({ required: true }) saveApplicationPartiesFn!: SaveApplicationPartiesFn;
  @Input({ required: true }) businessNumberSearchFn!: BusinessNumberSearchFn;
  @Input({ required: true }) downloadDocumentFromAzureFn!: DownloadDocumentFromAzureFn;
  @Input({ required: true }) uploadApplicationDocumentFn!: UpdateApplicationDocumentFn;
  @Input({ required: true }) listApplicationDocumentFn!: ListApplicationDocumentFn;
  @Input({ required: true }) application!: Application;

  @Input({ required: true }) updateIndividualKycStatusFn!: DigitalIdUpdateIndividualKycStatusFn;
  @Input() syncIndividualKycFromFrankieOneFn?: DigitalIdSyncIndividualKycFromFrankieOneFn;


  alerts: { type: 'warn', message: string }[] = [];

  individuals: IndividualInfo[] = [];
  individualsLoaded = false;

  /**
   * Details captured in the dialog for a KYB stakeholder being materialised as an individual for
   * the first time, keyed by stakeholderId. Drives the provisional row in the individuals table and
   * the `details` carried on the key-people save. Cleared on refresh (the save's success path).
   */
  pendingNewIndividuals = new Map<number, PendingNewIndividual>();

  /** Uploaded documents, read only to tell whether an individual's ID document is already on file. */
  applicationDocuments: AzureStorageDocument[] = [];

  formGroup: FormGroup<{
    individuals: KycVerificationIndividualsFormArray
  }>;
  formArray: KycVerificationIndividualsFormArray

  livenessPicture!: string;
  driverLicence!: string;

  constructor(private formBuilder: FormBuilder,
    private hotToastService: HotToastService,
    private dialogService: ApplicationDialogService,
    private portalHotToastService: PortalHotToastService,
    private digitalIdService: DigitalIdService,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private applicationService: ApplicationService,
    private ngZone: NgZone,
    private matDialog: MatDialog,
  ) {
    this.formArray = formBuilder.array<KycVerificationIndividualFormGroup>([]);
    this.formGroup = formBuilder.group({
      individuals: this.formArray,
    });
    this.placesSearchResultFn = this.authService.getGooglePlacesSearchResultFn;
    this.placesDetailsFn = this.authService.getGooglePlacesDetailsFn;
  }

  ngOnInit() {
    setupUntilDestroy(this);
    const companyId = this.application.OriginatorBusinessId;
    if (companyId) {
      const sub = this.getOriginatorByIdFn(companyId).pipe(
        this.portalHotToastService.topMenuLoadingObservable(),
        tap(r => {
          const o: OriginatorBusiness | null = r;
          if (o) {
            const relationship = o.Relationship;
            this.isBrokerDisclosed = !!(relationship && relationship.trim().toLowerCase() === 'disclosed');
          }
        })
      ).subscribe();
      this.subscriptions.push(sub);
    }
    this.refresh();

  }

  /**
   * @param onKybLoaded Runs once the reloaded business entities and individuals are both on screen.
   * For work that has to follow the reload rather than precede it, since the reload is what clears
   * anything staged. It waits for the individuals too because staging a tick matches the stakeholder
   * against them, and an empty list would stage somebody already on the application as a new row.
   */
  refresh(reconciliationDrafts: KybReconciliationDraftRestore[] = [], onKybLoaded?: () => void) {
    this.alerts = [];
    this.formArray.clear();
    this.individuals = [];
    this.pendingNewIndividuals.clear();
    this.individualsLoaded = false;
    let individualsReloaded = false;
    let kybReloaded = false;
    const runWhenBothReloaded = () => {
      if (individualsReloaded && kybReloaded) {
        onKybLoaded?.();
      }
    };
    this.getApplicationIndividualsFn(this.application.ApplicationId)
      .pipe(
        this.portalHotToastService.topMenuLoadingObservable(),
        // Without the individuals onKybLoaded never runs, so whatever it would have staged is lost.
        this.portalHotToastService.publishErrorNotificationObservable({
          errorTitle: 'Load individuals',
          errorMessage: 'Unable to load the individuals on this application',
          ...(onKybLoaded ? {errorSolution: 'Retry, then re-apply any ticks that are missing.'} : {}),
          retryFn: () => this.refresh(reconciliationDrafts, onKybLoaded),
        }),
        tap(r => {
          const individuals = r.individuals;
          this.addIndividuals(individuals);
          this.restorePendingReconciliationIndividuals(reconciliationDrafts);
          individualsReloaded = true;
          runWhenBothReloaded();
        }),
        finalize(() => {
          this.individualsLoaded = true;
        }),
      ).subscribe();
    // Refreshes the parties on the shared application object, which assigning the legacy Individuals
    // array used to do: the other panels on this page hold the same object and read `parties` off it,
    // so without this they show the pre-edit set. A separate call because the individuals endpoint
    // above returns the legacy shape only, and background for the same reason as the documents read
    // below - a failure here must not interrupt the listing.
    this.digitalIdService.getApplicationPartiesFn(this.application.ApplicationId).pipe(
      tap(payload => this.application.parties = payload),
    ).subscribe({ error: () => undefined });
    // Background read: the documents only feed the needs-attention flag, so a failure must not
    // interrupt the individuals listing with a toast.
    this.listApplicationDocumentFn(this.application.ApplicationId).pipe(
      tap(documents => this.applicationDocuments = documents ?? []),
    ).subscribe({ error: () => this.applicationDocuments = [] });
    this.kybEntitySummary?.refresh(reconciliationDrafts, onKybLoaded && (() => {
      kybReloaded = true;
      runWhenBothReloaded();
    }));
  }

  addIndividuals(individuals: IndividualWithResult[]) {
    const individualInfo: IndividualInfo[] = [];
    for (const individual of individuals) {
      const formControlIndividual: FormControl<IndividualFormValue> =
        this.formBuilder.control(this.toIndividualFormInitialValue(individual), [this.individualDetailsValidator()]);
      const formControlSignerRole: FormControl<SignerRoleTypes> = this.formBuilder.control(individualSignerRole(individual));
      const formControlContact: FormControl<string | null> = this.formBuilder.control(individual.isPrimaryContact ? 'primaryContact' : 'contact');
      const formArrayAdditionalRoles: FormArray<AdditionalRoleFormGroup> = this.formBuilder.array<AdditionalRoleFormGroup>([]);
      for (const additionalRole of individualEntityRoles(individual)) {
        const additionalRoleForm = this.formBuilder.group({
          business: [additionalRole.business, Validators.required],
          signerRole: [additionalRole.signerRole, Validators.required]
        })
        formArrayAdditionalRoles.push(additionalRoleForm);
      }
      if (!this.canEditAdditionalRoles()) {
        formArrayAdditionalRoles.disable();
      }
      const formGroup: KycVerificationIndividualFormGroup = this.formBuilder.group({
        individual: formControlIndividual,
        signerRole: formControlSignerRole,
        additionalRoles: formArrayAdditionalRoles,
        contact: formControlContact,
      });
      (formGroup as any)['individualWithResult'] = individual;
      this.formArray.push(formGroup);
      individualInfo.push({
        formGroup, individual
      });
    }
    this.individuals = individualInfo;
    // Through refreshIndividualsTable rather than straight to the data source: a refresh whose
    // business entities land before its individuals can already have staged a provisional row, and
    // listing the loaded individuals alone would drop it off the table.
    this.refreshIndividualsTable();
  }

  // Initial value for the nested <individual-form> control, built from the saved Individual record.
  // Country of citizenship is required (same as everywhere else this form is used), so a legacy
  // individual missing it loads as invalid and must be backfilled before that individual's row can
  // be saved - it does not block saving other, unrelated changed individuals.
  private toIndividualFormInitialValue(individual: IndividualWithResult): IndividualFormValue {
    return {
      title: apiIndividualTitle(individual),
      firstName: individual.givenName ?? '',
      middleName: individual.middleName ?? '',
      lastName: individual.surname ?? '',
      dob: apiIndividualDob(individual)!,
      hasAlias: !!individual.hasAlias,
      aliasNames: individual.aliasNames ?? null,
      countryOfCitizenship: countryOfCitizenshipByName(individual.countryOfCitizenship),
      email: individual.email ?? '',
      mobileNumber: individual.mobileNumber ?? '',
      residentialAddress: apiIndividualResidentialAddress(individual),
      propertyOwner: null,
      // the identification component only holds a value when both type and number exist, so a
      // half-captured record loads (and diffs) as empty rather than as a phantom change
      identification: (individual.identification?.type && individual.identification?.number)
        ? { idType: individual.identification.type, idNumber: individual.identification.number }
        : null,
      idDocument: null,
    };
  }

  /**
   * The person's details are edited in the popup now, so the inline <individual-form> that used to
   * report an incomplete record through the control is no longer rendered. Consent dispatch, the KYC
   * SMS and the section Save all still gate on completeness, so the same required set is enforced on
   * the control itself. Format (email/mobile) is left to the popup, which cannot be confirmed while
   * either is malformed.
   */
  private individualDetailsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.missingIndividualDetails(control.value as IndividualFormValue).length
        ? { individualForm: true }
        : null;
    };
  }

  private missingIndividualDetails(v: IndividualFormValue): string[] {
    const missing: string[] = [];
    if (!v?.title) missing.push('Title');
    if (!(v?.firstName ?? '').trim()) missing.push('First name');
    if (!(v?.lastName ?? '').trim()) missing.push('Last name');
    if (!v?.dob) missing.push('Date of birth');
    if (v?.hasAlias && !(v?.aliasNames ?? '').trim()) missing.push('Alias names');
    if (!v?.countryOfCitizenship) missing.push('Country of citizenship');
    if (!v?.residentialAddress) missing.push('Residential address');
    if (!(v?.email ?? '').trim()) missing.push('Email');
    if (!(v?.mobileNumber ?? '').trim()) missing.push('Mobile');
    if (!(v?.identification?.idType && v?.identification?.idNumber)) missing.push('Identification');
    return missing;
  }

  /**
   * Add a guarantor, of either kind, through the submission wizard's Additional Guarantors step.
   *
   * The entities the dialog collects are already persisted by the time it closes - that write is
   * what starts the company check the dialog then waits on - so what comes back to be applied is
   * the people: an individual guarantor is created here, trustees/partners entered against a trust
   * or partnership guarantor are persisted the same way, and the stakeholders ticked against an
   * entity are staged for the section Save exactly as a tick in the people table stages them.
   */
  onAddGuarantor() {
    this.matDialog.open<AddGuarantorDialog, AddGuarantorDialogData, AddGuarantorDialogResult>(
      AddGuarantorDialog,
      {
        data: {
          applicationId: this.application.ApplicationId,
          businessNumberSearchFn: this.businessNumberSearchFn,
          kybResultFn: this.applicationService.kybResultFn,
          kybRefreshFn: this.applicationService.kybRefreshFn,
          // The tab's own endpoint rather than the wizard's: it is internal-only and refuses the add
          // once the contract documents have gone out.
          kybInitiateCorporatePartyFn: input =>
            this.applicationService.addApplicationParty(input).pipe(map(r => r.payload)),
        },
        width: pxToRem(1000),
        maxWidth: '95vw',
        // A guarantor entity is persisted and its check started while the dialog is open, so a stray
        // backdrop click must not take the people ticked against it with it.
        disableClose: true,
      },
    ).afterClosed().pipe(
      tap((r?: AddGuarantorDialogResult) => {
        if (!r) {
          return;
        }
        if (r.cancelled) {
          // A guarantor entity is persisted while the dialog is open - that write is what starts its
          // company check - so even a cancel can leave something on the application to show.
          this.refresh();
          return;
        }
        this.applyAddedGuarantors(r);
      }),
    ).subscribe();
  }

  private applyAddedGuarantors(result: AddGuarantorDialogResult) {
    // Entity rows include the guarantors the dialog read back off the application, and every one of
    // them was persisted when its check started. People still need creating: an individual guarantor
    // row, and any trustees/partners typed against a trust/partnership guarantor. Those hang off
    // addedParties and are not persisted by the entity initiate, so without this they vanish on
    // Done and never appear under the trust.
    const individuals = (result.guarantors ?? []).filter(
      (guarantor): guarantor is IndividualGuarantor => guarantor.type === 'Individual',
    );
    const partyIndividuals = this.addedPartyIndividuals(result);
    const selections = result.kybSelections?.selections ?? [];
    const selectedStakeholderIds = new Set(result.kybSelections?.selectedStakeholderIds ?? []);
    const stageTicks = () => this.stageGuarantorKeyPeople(selections, selectedStakeholderIds);

    const creates = [
      ...individuals.map(guarantor => this.addIndividualFn(
        this.application.ApplicationId,
        this.toGuarantorIndividual(guarantor),
      )),
      ...partyIndividuals.map(party => this.addIndividualFn(
        this.application.ApplicationId,
        party,
      )),
    ];
    if (!creates.length) {
      this.refresh([], stageTicks);
      return;
    }
    forkJoin(creates).pipe(
      this.portalHotToastService.spinnerObservable(),
      this.portalHotToastService.snackBarObservable(
        creates.length > 1 ? 'Guarantors added' : 'Guarantor added',
      ),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: 'Add guarantor',
        errorMessage: 'Unable to add the guarantor',
        errorSolution: 'Check the individuals list, then retry.',
        retryFn: () => this.applyAddedGuarantors(result),
      }),
      tap(() => this.refresh([], stageTicks)),
    ).subscribe();
  }

  /**
   * Trustees/partners entered against a trust or partnership guarantor in the dialog. Corporate
   * parties are already persisted by the initiate call; only the natural-person rows need creating.
   */
  private addedPartyIndividuals(result: AddGuarantorDialogResult): AddIndividual[] {
    const groups: KybAddedParties[] = result.kybSelections?.addedParties ?? [];
    const out: AddIndividual[] = [];
    for (const group of groups) {
      const parties = Array.isArray(group.parties) ? group.parties : [];
      for (const party of parties) {
        const row = party as {
          type?: string;
          kind?: string;
          firstName?: string;
          middleName?: string;
          lastName?: string;
          dob?: unknown;
        };
        if (row.type !== 'Individual') {
          continue;
        }
        if (!(row.firstName ?? '').trim() || !(row.lastName ?? '').trim()) {
          continue;
        }
        out.push(this.toAddedPartyIndividual(row, group.commercialEntityId, group.entityType));
      }
    }
    return out;
  }

  /**
   * The guarantors step captures a name and date of birth for an individual and nothing else, the
   * rest being collected on the individual's own step. Post-submission there is no such step, so the
   * row arrives flagged as needing attention and is completed from the individuals table - which is
   * what the source says, and why no privacy consent form is issued until it is.
   */
  private toGuarantorIndividual(guarantor: IndividualGuarantor): AddIndividual {
    return {
      title: '',
      givenName: guarantor.firstName,
      middleName: guarantor.middleName,
      surname: guarantor.lastName,
      dateOfBirth: guarantor.dob.format('YYYY-MM-DD'),
      email: '',
      mobileNumber: '',
      isApplicant: false,
      isPrimaryContact: false,
      isGuarantor: true,
      // A personal guarantor holds no relationship to the borrower entity and does not sign: what
      // the legacy pair Role 'Guarantor' / SignerRole 'Guarantor' meant, which parsed to no role
      // flags and isSigner false.
      role: null,
      isSigner: false,
      // Null rather than a blank address: this dialog captures none, and the write shape keeps
      // "not captured" distinct from "captured as empty".
      homeAddress: null,
      property: { isOwner: null },
      source: PORTAL_GUARANTOR_INDIVIDUAL_SOURCE,
    };
  }

  /**
   * Same incomplete name+dob capture as an individual guarantor, hanging off the owning trust or
   * partnership so the KYC/AML tab can show them as trustees/partners under that entity.
   */
  private toAddedPartyIndividual(
    party: { kind?: string; firstName?: string; middleName?: string; lastName?: string; dob?: unknown },
    parentCommercialEntityId: number,
    entityType: string,
  ): AddIndividual {
    const role = party.kind === 'Partner' || (!party.kind && entityType === 'partnership')
      ? 'Partner' as const
      : 'Trustee' as const;
    return {
      title: '',
      givenName: party.firstName ?? '',
      middleName: party.middleName ?? '',
      surname: party.lastName ?? '',
      dateOfBirth: this.addedPartyDob(party.dob),
      email: '',
      mobileNumber: '',
      isApplicant: false,
      isPrimaryContact: false,
      role: roleFlagsFromApiRole(role),
      // The KYB trustee/partner form hard-ticks Guarantor and Signer and disables both.
      isSigner: true,
      isGuarantor: true,
      homeAddress: null,
      property: { isOwner: null },
      parentCommercialEntityId,
      source: PORTAL_GUARANTOR_INDIVIDUAL_SOURCE,
    };
  }

  private addedPartyDob(dob: unknown): string {
    if (dob == null || dob === '') {
      return '';
    }
    if (moment.isMoment(dob)) {
      return dob.format('YYYY-MM-DD');
    }
    const parsed = moment(dob);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  /**
   * Stage what the dialog ticked against its entities, once the reload has put those entities and
   * their stakeholders on screen.
   *
   * A stakeholder with no individual yet goes through the same path as a tick in the people table,
   * so a match against somebody already on the application is still offered and an unmatched one
   * still arrives needing attention. A tick on somebody already an individual is staged as a plain
   * role change.
   */
  private stageGuarantorKeyPeople(
    selections: KybPersonRoleSelection[],
    selectedStakeholderIds: ReadonlySet<number>,
  ) {
    for (const selection of selections) {
      if (!selectedStakeholderIds.has(selection.stakeholderId)) {
        continue;
      }
      const person = this.kybEntitySummary?.kybPersonFor(selection.stakeholderId);
      if (!person) {
        continue;
      }
      if (person.individualId == null) {
        this.onKeyPersonDetailsRequired({
          person,
          isGuarantor: selection.isGuarantor,
          isSigner: selection.isSigner,
        });
      } else if (
        person.isSelected !== true
        || person.isGuarantor !== selection.isGuarantor
        || person.isSigner !== selection.isSigner
      ) {
        this.applyTickToRow(person, true, selection.isGuarantor, selection.isSigner);
      }
    }
  }

  onUndoKybPersonResolution(person: KybPersonDetailsRequired['person']) {
    this.revertDisplacedKybPerson(person.stakeholderId);
  }

  /**
   * A Select/Guarantor/Signer tick on a KYB stakeholder with no application individual yet.
   *
   * Where somebody already on the application could be this same person, the dialog asks which -
   * on confirm the tick is applied and the choice staged, on cancel the tick is reverted. Where
   * there is no such candidate there is nothing to ask, so the tick stands and the individual is
   * staged from the KYB record alone, flagged in the individuals table for completion.
   */
  onKeyPersonDetailsRequired(event: KybPersonDetailsRequired) {
    const { person } = event;
    const existingPersonOptions = this.existingPersonOptionsFor(person);
    if (!existingPersonOptions.length) {
      this.stageKybPersonWithoutCandidates(event);
      return;
    }
    const initialRole: SignerRoleTypes =
      event.isGuarantor && event.isSigner ? 'GuarantorSigner'
        : event.isGuarantor ? 'Guarantor'
        : event.isSigner ? 'Signer'
        : null;
    this.dialogService.openIndividualFormDialog({
      existingIndividuals: this.allIndividualInfos().map(i => i.individual),
      businessSearchFn: this.businessNumberSearchFn,
      prefill: {
        firstName: person.givenName ?? '',
        lastName: person.surname ?? '',
        dob: person.dateOfBirth ? moment(person.dateOfBirth, 'YYYY-MM-DD') : null,
      },
      initialRole,
      existingPersonOptions,
    }).afterClosed().pipe(
      tap((r?: IndividualFormDialogResult) => {
        // Run inside Angular so the reverted tick / new row is reflected immediately.
        this.ngZone.run(() => {
          if (r && !r.cancelled && r.valid && r.applicationIndividualId != null) {
            const selectedRole = r.selectedRole ?? initialRole;
            const isGuarantor = selectedRole === 'Guarantor' || selectedRole === 'GuarantorSigner';
            const isSigner = selectedRole === 'Signer' || selectedRole === 'GuarantorSigner';
            const displaced = this.kybEntitySummary?.stageKybPersonLink(
              person.stakeholderId,
              r.applicationIndividualId,
            ) ?? [];
            for (const displacedStakeholderId of displaced) {
              this.revertDisplacedKybPerson(displacedStakeholderId);
            }
            const selectedIndividual = existingPersonOptions.find(
              option => option.individualId === r.applicationIndividualId,
            );
            person.individualId = selectedIndividual?.individualId ?? person.individualId;
            this.applyTickToRow(
              person,
              true,
              isGuarantor,
              isSigner,
              selectedIndividual?.applicationIndividualRoleId ?? undefined,
              selectedIndividual?.individualId,
            );
          } else if (r && !r.cancelled && r.valid && r.individual) {
            this.stagePendingNewIndividual(person, event, r.individual);
          } else {
            this.revertNewIndividualTick(person);
          }
        });
      })
    ).subscribe();
  }

  private existingPersonOptionsFor(person: KybPersonDetailsRequired['person']) {
    return this.kybEntitySummary?.reconciliationCandidates(person.stakeholderId) ?? [];
  }

  /**
   * A stakeholder with no reconciliation candidates to offer - e.g. a key person of a guarantor
   * entity just added, whose reconciliation has no application people of its own and so never needs
   * review. The save still adopts a unique identity match on the application (adoptIndividualByIdentity),
   * so link to that individual now instead of showing a new row that only merges into them on Save.
   *
   * Not for an entity under people review: there an empty candidate list can mean the match is already
   * linked to another stakeholder, and the review only accepts links to its own application people.
   */
  private stageKybPersonWithoutCandidates(event: KybPersonDetailsRequired) {
    const { person, isGuarantor, isSigner } = event;
    if (this.kybEntitySummary?.stakeholderRequiresReconciliation(person.stakeholderId)) {
      this.stageKybPersonAsNewIndividual(event);
      return;
    }
    const saved = this.individuals
      .map(info => info.individual)
      .filter(individual => !!individual.id && !individual.isDeleted);
    const match = pickIndividualMatch(person, saved, individual => individual);
    // Another stakeholder already linked to the match is an alias of the same person; linking this one
    // too would displace and untick it, so it stays a new row that the save merges into them.
    if (!match || this.kybEntitySummary?.isApplicationIndividualLinkedElsewhere(person.stakeholderId, match.id)) {
      this.stageKybPersonAsNewIndividual(event);
      return;
    }
    this.kybEntitySummary?.stageKybPersonLink(
      person.stakeholderId,
      match.id,
      `${match.givenName ?? ''} ${match.surname ?? ''}`.trim(),
    );
    person.individualId = match.id;
    this.applyTickToRow(person, true, isGuarantor, isSigner, undefined, match.id);
    this.refreshIndividualsTable();
  }

  /**
   * Whether this stakeholder is staged to be materialised from the KYB record alone, i.e. arriving as
   * a row that needs attention. A stakeholder who is already an individual, or whose details were
   * captured in a dialog, has nothing staged this way.
   */
  private stagedWithoutDetails(stakeholderId: number): boolean {
    const pending = this.pendingNewIndividuals.get(stakeholderId);
    return !!pending && !pending.details;
  }

  /**
   * Stage a ticked KYB stakeholder as a new individual without asking for anything. KYB gives a name
   * and date of birth and nothing else, so no details travel on the save and the server fills the
   * rest from the KYB record - the same row the Key People step writes on submission. What is still
   * missing is raised on the individuals row, which opens the edit form to complete it.
   */
  private stageKybPersonAsNewIndividual(event: KybPersonDetailsRequired) {
    const { person, isGuarantor, isSigner } = event;
    const signerRole: SignerRoleTypes = isGuarantor
      ? isSigner ? 'GuarantorSigner' : 'Guarantor'
      : isSigner ? 'Signer' : null;
    const provisional = this.buildKybProvisionalIndividual(person, signerRole, isGuarantor);
    this.pendingNewIndividuals.set(person.stakeholderId, {
      stakeholderId: person.stakeholderId,
      isSelected: true,
      isGuarantor,
      isSigner,
      idDocumentProvided: false,
      provisional,
      info: this.buildPendingIndividualInfo(provisional),
    });
    this.kybEntitySummary?.stageKybPersonAddition(
      person.stakeholderId,
      this.toReconciliationKybIdentity(provisional, isGuarantor, isSigner),
    );
    this.applyTickToRow(person, true, isGuarantor, isSigner);
    this.refreshIndividualsTable();
  }

  private stagePendingNewIndividual(
    person: KybPersonDetailsRequired['person'],
    event: KybPersonDetailsRequired,
    i: NonNullable<IndividualFormDialogResult['individual']>,
  ) {
    const add = i.residentialAddress ? parseRawAddress(i.residentialAddress.address) : null;
    const details: KeyPersonNewIndividualDetails = {
      title: i.title?.type ?? '',
      givenName: i.firstName,
      middleName: i.middleName ?? '',
      surName: i.lastName,
      dateOfBirth: i.dob.format('YYYY-MM-DD'),
      email: i.email ?? '',
      mobileNumber: i.mobile ?? '',
      residentialAddress: {
        address: i.residentialAddress?.address ?? '',
        unitNumber: add?.unit_number ?? '',
        streetNumber: add?.street_number ?? '',
        streetName: add?.street_name ?? '',
        streetType: add?.street_type ?? '',
        suburb: add?.locality ?? '',
        state: add?.region ?? '',
        postcode: add?.postal_code ?? '',
      },
      countryOfCitizenship: i.countryOfCitizenship?.name,
      hasAlias: i.hasAlias,
      aliasNames: i.hasAlias ? (i.aliasNames ?? undefined) : undefined,
      identificationType: i.identification?.idType,
      identificationNumber: i.identification?.idNumber,
    };
    const isGuarantor = i.signerRole === 'Guarantor' || i.signerRole === 'GuarantorSigner';
    const isSigner = i.signerRole === 'Signer' || i.signerRole === 'GuarantorSigner';
    const provisional = this.buildProvisionalIndividual(details, i.signerRole, isGuarantor);
    const info = this.buildPendingIndividualInfo(provisional);
    this.pendingNewIndividuals.set(person.stakeholderId, {
      stakeholderId: person.stakeholderId,
      details,
      isSelected: true,
      isGuarantor,
      isSigner,
      idDocumentProvided: !!i.idDocument,
      provisional,
      info,
    });
    this.kybEntitySummary?.stageKybPersonAddition(
      person.stakeholderId,
      this.toReconciliationNewPerson(i, isGuarantor, isSigner),
    );
    this.applyTickToRow(person, true, isGuarantor, isSigner);
    this.refreshIndividualsTable();
    this.expandedElement = provisional;
  }

  private toReconciliationNewPerson(
    individual: NonNullable<IndividualFormDialogResult['individual']>,
    isGuarantor: boolean,
    isSigner: boolean,
  ): KybManualPerson {
    return {
      clientId: v4(),
      individualId: null,
      applicationIndividualRoleId: null,
      commercialEntityId: 0,
      title: individual.title?.type ?? null,
      givenName: individual.firstName,
      middleName: individual.middleName || null,
      surname: individual.lastName,
      dateOfBirth: individual.dob.format('YYYY-MM-DD'),
      email: individual.email,
      mobileNumber: individual.mobile,
      hasAlias: individual.hasAlias,
      aliasNames: individual.aliasNames,
      countryOfCitizenship: individual.countryOfCitizenship?.name ?? null,
      residentialAddress: individual.residentialAddress,
      propertyOwner: false,
      propertyValue: null,
      mortgageBalance: null,
      propertyOwnedAddress: null,
      identificationType: individual.identification?.idType ?? null,
      identificationNumber: individual.identification?.idNumber ?? null,
      idDocumentProvided: !!individual.idDocument,
      isSigner,
      isGuarantor,
    };
  }

  /**
   * Add a corporate trustee or partner under a trust or partnership. Opens the ABN-search dialog; on
   * confirm the internal-only endpoint persists the entity and starts its KYB run, and the summary
   * refreshes to show the pending run. Individuals are not created here - they are ticked from the
   * KYB stakeholders once the run completes. Guarantors are added from Add Guarantor instead, which
   * waits for the run rather than leaving it to be found later.
   */
  onAddParty(owner: KybEntitySummary) {
    const ownerEntities = (this.kybEntitySummary?.rows ?? [])
      .map(row => row.entity)
      .filter(entity => entity.entityType === 'trust' || entity.entityType === 'partnership');
    const fixedRole = owner.entityType === 'trust' ? 'trustee' : 'partner';
    this.dialogService.openAddApplicationPartyDialog({
      businessNumberSearchFn: this.businessNumberSearchFn,
      ownerEntities,
      fixedRole,
      fixedOwnerId: owner.commercialEntityId,
    }).afterClosed().pipe(
      tap((r?: AddApplicationPartyDialogResult) => {
        if (!r || r.cancelled || !r.valid || !r.party) {
          return;
        }
        const party = r.party;
        this.applicationService.addApplicationParty({
          applicationId: this.application.ApplicationId,
          parentCommercialEntityId: party.parentCommercialEntityId ?? owner.commercialEntityId,
          role: party.role,
          legalName: party.legalName,
          abn: party.abn,
          acn: party.acn,
          entityType: party.entityType,
        }).pipe(
          this.portalHotToastService.spinnerObservable(),
          this.portalHotToastService.snackBarObservable('Entity added, company check started'),
          this.portalHotToastService.publishErrorNotificationObservable({
            errorTitle: 'Add entity',
            errorMessage: 'Unable to add the entity',
            retryFn: () => this.onAddParty(owner),
          }),
          tap(() => this.kybEntitySummary?.refresh()),
        ).subscribe();
      }),
    ).subscribe();
  }

  /**
   * Add a natural-person trustee/partner under a trust or partnership. Same name+DOB capture as
   * Additional Guarantors / Add Guarantor; the rest is completed from the individuals table.
   * existingPeople is left unset so a director already on the application can still be declared
   * trustee of this entity — addApplicationIndividual attaches the role to that person.
   */
  onAddIndividualParty(owner: KybEntitySummary) {
    const kind = owner.entityType === 'trust' ? 'Trustee' : 'Partner';
    this.dialogService.openKybIndividualTrusteeDialog({
      title: 'Add Individual',
      firstName: null,
      middleName: null,
      lastName: null,
      dob: null,
    }).afterClosed().pipe(
      tap((result?: KybIndividualTrusteeDialogResult) => {
        if (!result) {
          return;
        }
        this.persistIndividualParty(owner, kind, result);
      }),
    ).subscribe();
  }

  private persistIndividualParty(
    owner: KybEntitySummary,
    kind: 'Trustee' | 'Partner',
    result: KybIndividualTrusteeDialogResult,
  ) {
    const payload = this.toAddedPartyIndividual(
      {
        kind,
        firstName: result.firstName,
        middleName: result.middleName ?? '',
        lastName: result.lastName,
        dob: result.dob,
      },
      owner.commercialEntityId,
      owner.entityType,
    );
    this.addIndividualFn(this.application.ApplicationId, payload).pipe(
      this.portalHotToastService.spinnerObservable(),
      this.portalHotToastService.snackBarObservable(`${kind} added`),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: `Add ${kind.toLowerCase()}`,
        errorMessage: `Unable to add the ${kind.toLowerCase()}`,
        retryFn: () => this.persistIndividualParty(owner, kind, result),
      }),
      tap(() => this.refresh()),
    ).subscribe();
  }

  /**
   * Stage a non-primary entity for removal. Applied on the tab-level Save; the row is marked pending
   * until then so the reviewer can undo without a second confirmation.
   */
  onRemoveParty(entity: KybEntitySummary) {
    if (entity.isPrimary) {
      this.dialogService.openAlertDialog({
        message: 'Unable to remove',
        subMessage: 'The primary borrowing entity cannot be removed.',
      });
      return;
    }
    this.kybEntitySummary?.stageEntityRemoval(entity);
  }

  /** Undo a staged entity removal before Save. */
  onUndoRemoveParty(entity: KybEntitySummary) {
    this.kybEntitySummary?.unstageEntityRemoval(entity.commercialEntityId);
  }

  /**
   * Stage an individual trustee/partner for removal. Applied on the tab-level Save; the row is
   * marked pending until then so the reviewer can undo. The structural guard is enforced on Save.
   */
  onRemoveIndividualParty(party: KybEntityIndividualPartySummary) {
    this.kybEntitySummary?.stageIndividualRemoval(party);
  }

  // Builds the unsaved row shown in the individuals table. No id / sfContactId / verification
  // result yet; those only exist once the save materialises the record.
  private buildProvisionalIndividual(
    details: KeyPersonNewIndividualDetails,
    signerRole: SignerRoleTypes,
    isGuarantor: boolean,
  ): IndividualWithResult {
    return {
      ...provisionalIdentity(signerRole, isGuarantor),
      title: details.title,
      givenName: details.givenName,
      middleName: details.middleName ?? '',
      surname: details.surName,
      dateOfBirth: details.dateOfBirth,
      email: details.email,
      mobileNumber: details.mobileNumber,
      homeAddress: {
        unformattedAddress: details.residentialAddress.address ?? '',
        unitNumber: details.residentialAddress.unitNumber ?? '',
        streetNumber: details.residentialAddress.streetNumber ?? '',
        streetName: details.residentialAddress.streetName ?? '',
        streetType: details.residentialAddress.streetType ?? '',
        suburb: details.residentialAddress.suburb ?? '',
        state: details.residentialAddress.state ?? '',
        postcode: details.residentialAddress.postcode ?? '',
        isManualInput: false,
      },
      hasAlias: details.hasAlias,
      aliasNames: details.aliasNames,
      countryOfCitizenship: details.countryOfCitizenship,
      identification: { type: details.identificationType, number: details.identificationNumber },
    };
  }

  // The unsaved row for a stakeholder ticked straight through. KYB knows their name and date of
  // birth; the rest stays blank until the edit form is used, which is what the needs-attention flag
  // on the row asks for.
  private buildKybProvisionalIndividual(
    person: KybPersonDetailsRequired['person'],
    signerRole: SignerRoleTypes,
    isGuarantor: boolean,
  ): IndividualWithResult {
    return {
      ...provisionalIdentity(signerRole, isGuarantor),
      title: '',
      givenName: person.givenName ?? '',
      middleName: '',
      surname: person.surname ?? '',
      dateOfBirth: person.dateOfBirth ?? '',
      email: '',
      mobileNumber: '',
      hasAlias: false,
    };
  }

  // Reflects the confirmed tick back onto the KYB table row and records it as a dirty key-person
  // change, so the row shows the tick and the save carries it.
  private applyTickToRow(
    person: KybPersonDetailsRequired['person'],
    isSelected: boolean,
    isGuarantor: boolean,
    isSigner: boolean,
    applicationIndividualRoleId?: number,
    applicationIndividualId?: number,
  ) {
    person.isGuarantor = isGuarantor;
    person.isSigner = isSigner;
    person.isSelected = isSelected;
    this.kybEntitySummary?.applyConfirmedTick(person, isSelected, isGuarantor, isSigner);
    this.kybEntitySummary?.onKeyPersonChange({
      stakeholderId: person.stakeholderId,
      isSelected,
      isGuarantor,
      isSigner,
      ...(applicationIndividualId != null ? {applicationIndividualId} : {}),
      ...(applicationIndividualRoleId != null ? {applicationIndividualRoleId} : {}),
    });
  }

  // Reads the provisional rows' expanded-form values back into their staged details, so an edit
  // made after the dialog (e.g. fixing a typo) is what gets saved.
  private syncPendingDetailsFromForms(keyPeople: KybPersonRowState[]) {
    const keyPeopleByStakeholder = new Map(keyPeople.map(state => [state.stakeholderId, state]));
    for (const pending of this.pendingNewIndividuals.values()) {
      const state = keyPeopleByStakeholder.get(pending.stakeholderId);
      if (state) {
        pending.isSelected = state.isSelected;
        pending.isGuarantor = state.isGuarantor;
        pending.isSigner = state.isSigner;
      }
      // Only complete details are sent. A row still short of something carries nothing but the KYB
      // identity, and the server materialises it from the KYB record rather than from these blanks.
      const v = pending.info.formGroup.controls.individual.value;
      if (v && !this.missingIndividualDetails(v).length) {
        pending.details = this.toKeyPersonDetails(v);
      }
      this.kybEntitySummary?.stageKybPersonAddition(
        pending.stakeholderId,
        this.toReconciliationPendingPerson(pending),
      );
    }
  }

  // Only called for a form value that missingIndividualDetails reports as complete, so every field
  // it requires is present.
  private toKeyPersonDetails(v: NonNullable<IndividualFormValue>): KeyPersonNewIndividualDetails {
    const parsed = v.residentialAddress ? parseRawAddress(v.residentialAddress.address) : null;
    return {
      title: v.title?.type ?? '',
      givenName: v.firstName,
      middleName: v.middleName ?? '',
      surName: v.lastName,
      dateOfBirth: v.dob.format('YYYY-MM-DD'),
      email: v.email ?? '',
      mobileNumber: v.mobileNumber ?? '',
      residentialAddress: {
        address: v.residentialAddress?.address ?? '',
        unitNumber: parsed?.unit_number ?? '',
        streetNumber: parsed?.street_number ?? '',
        streetName: parsed?.street_name ?? '',
        streetType: parsed?.street_type ?? '',
        suburb: parsed?.locality ?? '',
        state: parsed?.region ?? '',
        postcode: parsed?.postal_code ?? '',
      },
      countryOfCitizenship: v.countryOfCitizenship?.name,
      hasAlias: !!v.hasAlias,
      aliasNames: v.hasAlias ? (v.aliasNames ?? undefined) : undefined,
      identificationType: v.identification?.idType,
      identificationNumber: v.identification?.idNumber,
    };
  }

  /**
   * The reconciliation addition for a stakeholder ticked without a dialog. Only the KYB identity is
   * known, and the contact fields are left empty rather than guessed - the server materialises the
   * individual from the KYB record, so there is nothing here for them to overwrite.
   */
  private toReconciliationKybIdentity(
    individual: IndividualWithResult,
    isGuarantor: boolean,
    isSigner: boolean,
  ): KybManualPerson {
    return {
      clientId: v4(),
      individualId: null,
      applicationIndividualRoleId: null,
      commercialEntityId: 0,
      title: null,
      givenName: individual.givenName ?? '',
      middleName: null,
      surname: individual.surname ?? '',
      dateOfBirth: individual.dateOfBirth ?? '',
      email: '',
      mobileNumber: '',
      hasAlias: false,
      aliasNames: null,
      countryOfCitizenship: null,
      residentialAddress: null,
      propertyOwner: false,
      propertyValue: null,
      mortgageBalance: null,
      propertyOwnedAddress: null,
      identificationType: null,
      identificationNumber: null,
      idDocumentProvided: false,
      isSigner,
      isGuarantor,
    };
  }

  private toReconciliationPendingPerson(pending: PendingNewIndividual): KybManualPerson {
    const details = pending.details;
    if (!details) {
      return this.toReconciliationKybIdentity(pending.provisional, pending.isGuarantor, pending.isSigner);
    }
    return {
      clientId: v4(),
      individualId: null,
      applicationIndividualRoleId: null,
      commercialEntityId: 0,
      title: details.title || null,
      givenName: details.givenName,
      middleName: details.middleName || null,
      surname: details.surName,
      dateOfBirth: details.dateOfBirth,
      email: details.email,
      mobileNumber: details.mobileNumber,
      hasAlias: !!details.hasAlias,
      aliasNames: details.aliasNames ?? null,
      countryOfCitizenship: details.countryOfCitizenship ?? null,
      residentialAddress: {
        address: details.residentialAddress.address ?? '',
        UnitNumber: details.residentialAddress.unitNumber ?? '',
        StreetNumber: details.residentialAddress.streetNumber ?? '',
        StreetName: details.residentialAddress.streetName ?? '',
        StreetType: details.residentialAddress.streetType ?? '',
        Suburb: details.residentialAddress.suburb ?? '',
        State: details.residentialAddress.state ?? '',
        Postcode: details.residentialAddress.postcode ?? '',
      },
      propertyOwner: false,
      propertyValue: null,
      mortgageBalance: null,
      propertyOwnedAddress: null,
      identificationType: details.identificationType ?? null,
      identificationNumber: details.identificationNumber ?? null,
      idDocumentProvided: pending.idDocumentProvided,
      isSigner: pending.isSigner,
      isGuarantor: pending.isGuarantor,
    };
  }

  // Form group for the provisional row's expanded editor, mirroring addIndividuals but without a
  // saved record. Editing here updates the staged details read on save.
  private buildPendingIndividualInfo(provisional: IndividualWithResult): IndividualInfo {
    const formControlIndividual: FormControl<IndividualFormValue> =
      this.formBuilder.control(this.toIndividualFormInitialValue(provisional), [this.individualDetailsValidator()]);
    const formControlSignerRole: FormControl<SignerRoleTypes> = this.formBuilder.control(individualSignerRole(provisional));
    const formControlContact: FormControl<string | null> = this.formBuilder.control(provisional.isPrimaryContact ? 'primaryContact' : 'contact');
    const formArrayAdditionalRoles: FormArray<AdditionalRoleFormGroup> = this.formBuilder.array<AdditionalRoleFormGroup>([]);
    const formGroup: KycVerificationIndividualFormGroup = this.formBuilder.group({
      individual: formControlIndividual,
      signerRole: formControlSignerRole,
      additionalRoles: formArrayAdditionalRoles,
      contact: formControlContact,
    });
    (formGroup as any)['individualWithResult'] = provisional;
    return { formGroup, individual: provisional };
  }

  private restorePendingReconciliationIndividuals(drafts: KybReconciliationDraftRestore[]): void {
    for (const draft of drafts) {
      for (const action of draft.data.actions) {
        if (
          action.action !== 'ADD'
          || action.applicationKybStakeholderId == null
          || !action.newPerson
        ) {
          continue;
        }
        const person = action.newPerson;
        const details: KeyPersonNewIndividualDetails = {
          title: person.title ?? '',
          givenName: person.givenName,
          middleName: person.middleName ?? '',
          surName: person.surname,
          dateOfBirth: person.dateOfBirth,
          email: person.email,
          mobileNumber: person.mobileNumber,
          residentialAddress: {
            address: person.residentialAddress?.address ?? '',
            unitNumber: person.residentialAddress?.UnitNumber ?? '',
            streetNumber: person.residentialAddress?.StreetNumber ?? '',
            streetName: person.residentialAddress?.StreetName ?? '',
            streetType: person.residentialAddress?.StreetType ?? '',
            suburb: person.residentialAddress?.Suburb ?? '',
            state: person.residentialAddress?.State ?? '',
            postcode: person.residentialAddress?.Postcode ?? '',
          },
          countryOfCitizenship: person.countryOfCitizenship ?? undefined,
          hasAlias: person.hasAlias,
          aliasNames: person.aliasNames ?? undefined,
          identificationType: person.identificationType ?? undefined,
          identificationNumber: person.identificationNumber ?? undefined,
        };
        const signerRole: SignerRoleTypes = person.isGuarantor
          ? person.isSigner ? 'GuarantorSigner' : 'Guarantor'
          : person.isSigner ? 'Signer' : null;
        const provisional = this.buildProvisionalIndividual(details, signerRole, person.isGuarantor);
        const info = this.buildPendingIndividualInfo(provisional);
        this.pendingNewIndividuals.set(action.applicationKybStakeholderId, {
          stakeholderId: action.applicationKybStakeholderId,
          // A stakeholder ticked without a dialog was staged with no details at all, so the retry
          // must not send these blanks in place of what the KYB record holds.
          ...((person.email ?? '').trim() ? { details } : {}),
          isSelected: true,
          isGuarantor: person.isGuarantor,
          isSigner: person.isSigner,
          idDocumentProvided: person.idDocumentProvided,
          provisional,
          info,
        });
      }
    }
    this.refreshIndividualsTable();
  }

  // Cancel path: drop anything staged for the row and put the held-back tick back to unticked.
  private revertNewIndividualTick(person: KybPersonDetailsRequired['person']) {
    this.pendingNewIndividuals.delete(person.stakeholderId);
    this.kybEntitySummary?.clearKybPersonResolution(person.stakeholderId);
    this.kybEntitySummary?.revertTick(person);
    this.refreshIndividualsTable();
  }

  isProvisionalIndividual(element: IndividualWithResult): boolean {
    return !element.guid && [...this.pendingNewIndividuals.values()].some(p => p.provisional === element);
  }

  // Always false, and has been since AZ-1439 added it: the predicate required an empty SignerRole,
  // but the projection falls that back to 'Others' (application-parties-utils.ts:1283), so the
  // "Internal review required" banner has never rendered. The API shape can express the intent -
  // roles.length === 0 && !isGuarantor - but switching it on is a visible change for internal users,
  // so it stays inert here rather than being revived inside a migration.
  requiresPartyRelationshipReview(_element: IndividualWithResult): boolean {
    return false;
  }

  // Rebuilds the table with the saved individuals plus one provisional row per pending person.
  private refreshIndividualsTable() {
    const provisionals = [...this.pendingNewIndividuals.values()].map(p => p.provisional);
    this.dataSource.update([...this.individuals.map(i => i.individual), ...provisionals]);
  }


  _sendVerificationSmsToIndividual(individual: IndividualWithResult) {
    // Individuals are now saved together via the section-level Save button, so there is no inline
    // save here - the officer must save first, otherwise the SMS would go out against stale details.
    if (this.changedIndividuals().length > 0) {
      this.dialogService.openAlertDialog({
        message: 'Unsaved changes',
        subMessage: 'Please save your changes before sending the KYC verification SMS.',
      });
      return;
    }
    this.sendVerificationSmsToIndividual(individual);
  }

  sendVerificationSmsToIndividual = (individual: IndividualWithResult) => {
    if (!individual.guid) {
      this.dialogService.openAlertDialog({
        message: 'Error',
        subMessage: `Individual ${individual.givenName} ${individual.surname} do not have an id, cannot send out SMS`
      });
      return;
    }
    if (!individual.mobileNumber) {
      this.dialogService.openAlertDialog({ message: 'Error', subMessage: `Individual ${individual.givenName} ${individual.surname} do not have a mobile number` });
      return;
    }

    this.sendVerificationSmsFn(this.application.ApplicationId, individual.guid)
      .pipe(
        this.loader.sendVerificationSms.track(),
        tap(r => {
          this.dialogService.successDialog({
            message: 'Success',
            subMessage: 'KYC verification SMS sent to individual successfully. The link will expire in 24 hours.'
          });
          this.refresh();
        })
      ).subscribe();
  }

  individualFormGroup(individual: IndividualWithResult): KycVerificationIndividualFormGroup | undefined {
    const individualInfo = this.findIndividualInfo(individual);
    return individualInfo?.formGroup;
  }

  // Saved individuals are matched by id; a provisional (not yet saved) one has no id, so it is
  // matched against the pending map by object identity.
  private findIndividualInfo(individual: IndividualWithResult): IndividualInfo | undefined {
    if (individual.guid) {
      return this.individuals.find(i => i?.individual?.guid === individual.guid);
    }
    const pending = [...this.pendingNewIndividuals.values()].find(p => p.provisional === individual);
    return pending?.info;
  }

  /**
   * Edit an individual in the same popup the Add button uses. The confirmed values are staged on the
   * row's form group only: the section Save is what writes them, so an edit is still abandoned by
   * leaving the tab without saving.
   */
  editIndividual(individual: IndividualWithResult) {
    const info = this.findIndividualInfo(individual);
    if (!info) {
      return;
    }
    this.dialogService.openIndividualFormDialog({
      // This individual is left out so the popup does not report them as a duplicate of themselves.
      existingIndividuals: this.allIndividualInfos().filter(other => other !== info).map(other => other.individual),
      businessSearchFn: this.businessNumberSearchFn,
      editIndividual: info.formGroup.controls.individual.value,
      initialRole: info.formGroup.controls.signerRole.value,
      // An edit normally leaves the ID document alone, but a KYB-materialised row was never asked
      // for one, so this is where it is supplied. Brokers completing a legacy individual that is
      // missing mandatory fields use the same upload. Where one is already on file the upload is
      // offered rather than demanded, so a row flagged for some other missing field does not force a
      // pointless re-upload - and the documents already held are listed under it, which is the only
      // place in this flow the reviewer can see them.
      showIdDocumentUpload: this.needsAttention(individual) || this.brokerCompletesIncompleteRow(individual),
      idDocumentProvided: this.hasIdDocument(individual),
      existingIdDocuments: this.idDocumentsOnFile(individual),
    }).afterClosed().pipe(
      tap((r?: IndividualFormDialogResult) => {
        if (!r || r.cancelled || !r.valid || !r.individualFormValue) {
          return;
        }
        info.formGroup.controls.individual.setValue(r.individualFormValue);
        info.formGroup.controls.signerRole.setValue(r.individual?.signerRole ?? null);
      }),
    ).subscribe();
  }

  // ---------------------------------------------------------------- staged (unsaved) row values
  //
  // The table and the expanded summary read from the row's form group rather than the loaded record,
  // so an edit staged in the popup is visible before the section Save writes it.

  private stagedValue(individual: IndividualWithResult): IndividualFormValue {
    return this.findIndividualInfo(individual)?.formGroup.controls.individual.value ?? null;
  }

  displayName(individual: IndividualWithResult): string {
    const v = this.stagedValue(individual);
    return `${v?.firstName ?? individual.givenName ?? ''} ${v?.lastName ?? individual.surname ?? ''}`.trim();
  }

  /** Expanded-row `{NAME} - {ROLE}` header. Preview (collapsed Name column) stays on displayName. */
  displayHeaderName(individual: IndividualWithResult): string {
    const v = this.stagedValue(individual);
    return joinPersonName(
      v?.firstName ?? individual.givenName,
      v?.middleName ?? individual.middleName,
      v?.lastName ?? individual.surname,
    );
  }

  displayDob(individual: IndividualWithResult): string {
    // individualDob returns an invalid Moment rather than null for a record with no date of birth.
    const dob = this.stagedValue(individual)?.dob ?? (individual.dateOfBirth ? moment(individual.dateOfBirth, 'YYYY-MM-DD') : null);
    return dob?.isValid() ? dob.format('DD/MM/YYYY') : '';
  }

  displaySignerRole(individual: IndividualWithResult): SignerRoleTypes {
    return this.findIndividualInfo(individual)?.formGroup.controls.signerRole.value ?? individualSignerRole(individual);
  }

  displayAddress(individual: IndividualWithResult): string {
    const address = this.stagedValue(individual)?.residentialAddress;
    if (!address) {
      return individual.homeAddress?.unformattedAddress ?? '';
    }
    // Same fallback as the address pipe: a record captured field by field carries no unformatted line.
    return (address.address ?? '').trim() || parseAddressComponentsToString(
      address.UnitNumber, address.StreetNumber, address.StreetName, address.StreetType,
      address.Suburb, address.State, address.Postcode,
    );
  }

  /**
   * The read-only fields shown in the expanded row. Everything but the Salesforce contact ID is
   * edited through the popup, so it is read from the staged value.
   */
  individualSummary(individual: IndividualWithResult): { label: string, value: string, full?: boolean }[] {
    const v = this.stagedValue(individual);
    return [
      { label: 'Salesforce contact ID', value: individual.sfContactId ?? '' },
      { label: 'Email', value: v?.email ?? individual.email ?? '' },
      { label: 'Mobile', value: v?.mobileNumber ?? individual.mobileNumber ?? '' },
      { label: 'Date of birth', value: this.displayDob(individual) },
      { label: 'Country of citizenship', value: v?.countryOfCitizenship?.name ?? individual.countryOfCitizenship ?? '' },
      {
        label: this.identificationLabel(v?.identification?.idType ?? individual.identification?.type),
        value: v?.identification?.idNumber ?? individual.identification?.number ?? '',
      },
      { label: 'Residential address', value: this.displayAddress(individual), full: true },
    ].map(field => ({ ...field, value: field.value || 'Not available' }));
  }

  /** The identification number's label carries its type, eg. "Driver's Licence number". */
  private identificationLabel(idType: string | null | undefined): string {
    const label = getIdTypes().find(type => type.value === idType)?.label;
    return label ? `${label} number` : 'Identification number';
  }

  /**
   * Whether the reviewer still has to complete this individual.
   *
   * Read from ApplicationIndividual.needsAttention rather than recomputed from empty fields. The
   * flag is raised only for a row that arrived knowing less than a whole individual - materialised
   * from a KYB tick, or added through Add Guarantor - and the server lowers it once the record is
   * complete. Deriving it instead flagged production individuals for optional fields nobody was
   * ever asked for.
   *
   * A row a provisional save has staged but not yet written has no persisted flag to read, so it
   * falls back to the details actually captured.
   */
  needsAttention(individual: IndividualWithResult): boolean {
    if (individual.isDeleted) {
      return false;
    }
    if (!individual.guid) {
      return isIncompleteOnArrivalSource(individual.source) && this.missingDetailsFor(individual).length > 0;
    }
    return !!individual.needsAttention;
  }

  /**
   * Whether the row can be expanded. Only a row whose form has never been saved cannot: there is
   * nothing to expand into yet, so the click opens the form that completes it. Once saved the row
   * expands for good, flagged or not - the flag is information, not a lock.
   *
   * Brokers also cannot expand a saved row that is still missing mandatory fields. Those
   * individuals were submitted before the fields existed, so they are not flagged needs-attention;
   * the same click-to-edit is how they get filled in. Internal staff keep expanding - they have
   * the Edit menu.
   */
  canExpand(individual: IndividualWithResult): boolean {
    if (individual.detailsSaved === false) {
      return false;
    }
    return !this.brokerCompletesIncompleteRow(individual);
  }

  /**
   * Brokers completing a saved individual that is still missing mandatory fields. These rows
   * were submitted before the fields existed, so they are not flagged needs-attention.
   */
  private brokerCompletesIncompleteRow(individual: IndividualWithResult): boolean {
    return !isInternalUser(this.loggedInUser)
      && !individual.isDeleted
      && this.missingDetailsFor(individual).length > 0;
  }

  /** What the edit form has to supply, for the flag's tooltip. */
  needsAttentionTooltip(individual: IndividualWithResult): string {
    const missing = this.missingDetailsFor(individual);
    if (!this.hasIdDocument(individual)) {
      missing.push('ID document');
    }
    const action = this.canExpand(individual)
      ? 'Use Edit to complete them.'
      : 'Click the row to complete them.';
    return missing.length
      ? `Needs attention - missing ${missing.join(', ')}. ${action}`
      : `Needs attention. ${action}`;
  }

  private missingDetailsFor(individual: IndividualWithResult): string[] {
    const info = this.findIndividualInfo(individual);
    return info ? this.missingIndividualDetails(info.formGroup.controls.individual.value) : [];
  }

  // A licence attached in the edit form counts before it is uploaded, so completing a row reads as
  // done without waiting for the save.
  private hasIdDocument(individual: IndividualWithResult): boolean {
    const info = this.findIndividualInfo(individual);
    if ((info?.formGroup.controls.individual.value?.idDocument ?? []).length) {
      return true;
    }
    return this.idDocumentsOnFile(individual).length > 0;
  }

  /** ID documents filed against this individual, for listing under the upload box. */
  idDocumentsOnFile(individual: IndividualWithResult): UploadedDocumentRef[] {
    return individualIdDocumentRefs(
      individual.idDocuments,
      this.applicationDocuments,
      [individual.guid],
      this.displayName(individual),
    );
  }

  /** Whether this row carries an edit the section Save has not written yet. */
  hasUnsavedEdits(individual: IndividualWithResult): boolean {
    return !!individual.guid
      && this.individuals.some(i => i.individual.guid === individual.guid)
      && Object.keys(this.getFormControlChanges(individual)).length > 0;
  }

  // Diffs the nested <individual-form> value plus the KYC-specific controls against the loaded
  // Individual record. The diff itself lives in kycIndividualChanges (shared library), which
  // normalises the loaded side the same way the form does so an untouched record never reads as
  // changed - a phantom-changed legacy record that is also invalid would otherwise hold the
  // section-level Save disabled no matter which individual was edited.
  getFormControlChanges(individual: IndividualWithResult) {
    const individualInfo = this.individuals.find(i => i?.individual?.guid === individual.guid);
    if (!individualInfo) {
      throw new Error(`Individual with id ${individual.guid} not found`);
    }

    const formGroup = individualInfo.formGroup;
    return kycIndividualChanges(individualInfo.individual, {
      individual: formGroup.controls.individual.value,
      signerRole: formGroup.controls.signerRole.value,
      contact: formGroup.controls.contact.value,
      additionalRoles: formGroup.controls.additionalRoles.value ?? [],
    }, individualEntityRoles(individualInfo.individual));
  }

  // Individuals whose form values actually differ from their loaded values (the section-level Save
  // persists all of these together). We compare values via getFormControlChanges rather than trust
  // the Angular `dirty` flag, because several custom form controls (name / address / date pickers)
  // mark themselves dirty on load - which would otherwise enable Save with no real change.
  private changedIndividuals(): IndividualInfo[] {
    return this.individuals.filter(info =>
      !!info.individual.guid && Object.keys(this.getFormControlChanges(info.individual)).length > 0,
    );
  }

  // Entity company-block edits made in the Business Entities section, saved in the same request.
  private dirtyEntities(): SaveApplicationPartyEntityInput[] {
    return this.kybEntitySummary?.dirtyEntities() ?? [];
  }

  private dirtyKeyPeople(): KybPersonRowState[] {
    return this.kybEntitySummary?.dirtyKeyPeople() ?? [];
  }

  private dirtyReconciliations(): KybReconciliationSaveData[] {
    return this.kybEntitySummary?.dirtyReconciliations() ?? [];
  }

  applicationReconciliationReviews(individual: IndividualWithResult): KybApplicationReconciliationReview[] {
    return this.kybEntitySummary?.applicationReconciliationReviewsFor(individual.guid) ?? [];
  }

  stageApplicationReconciliationDecision(
    event: MouseEvent,
    review: KybApplicationReconciliationReview,
    decision: 'KEEP' | 'REMOVE',
  ): void {
    event.stopPropagation();
    this.kybEntitySummary?.setApplicationReconciliationDecision(review.entity, review.person, decision);
  }

  /**
   * Selected stakeholders with no application individual and no staged details — typically a locked
   * 100% owner who was selected by default and never opened the dialog. The save cannot proceed
   * until their details are captured.
   */
  private selectedNewStakeholdersMissingDetails(keyPeople: KybPersonRowState[]): KybPersonDetailsRequired[] {
    const missing: KybPersonDetailsRequired[] = [];
    for (const state of keyPeople) {
      if (!state.isSelected || this.pendingNewIndividuals.has(state.stakeholderId)) {
        continue;
      }
      // A staged existing-person link or new-person add already captured details; the loaded
      // stakeholder copy still has individualId null, so it must not re-open the dialog on Save.
      if (
        state.applicationIndividualRoleId != null
        || this.kybEntitySummary?.hasStagedKybResolution(state.stakeholderId)
      ) {
        continue;
      }
      const person = this.kybEntitySummary?.liveKybPerson(state.stakeholderId)
        ?? this.findKybPerson(state.stakeholderId);
      if (person && person.individualId == null) {
        missing.push({ person, isGuarantor: state.isGuarantor, isSigner: state.isSigner });
      }
    }
    return missing;
  }

  private revertDisplacedKybPerson(stakeholderId: number): void {
    const person = this.kybEntitySummary?.liveKybPerson(stakeholderId)
      ?? this.findKybPerson(stakeholderId);
    if (!person) return;
    person.individualId = null;
    person.isGuarantor = false;
    person.isSigner = false;
    person.isSelected = false;
    this.pendingNewIndividuals.delete(stakeholderId);
    this.kybEntitySummary?.clearKybPersonResolution(stakeholderId);
    this.kybEntitySummary?.revertTick(person);
    this.refreshIndividualsTable();
  }

  private findKybPerson(stakeholderId: number) {
    for (const row of this.kybEntitySummary?.rows ?? []) {
      for (const entity of [row.entity, ...(row.entity.children ?? [])]) {
        const person = kybEntityAsKeyPeopleResult(entity).people.find(p => p.stakeholderId === stakeholderId);
        if (person) {
          return person;
        }
      }
    }
    return null;
  }

  // Used by parent application-details tabs to warn before leaving KYC/AML with unsaved edits.
  hasUnsavedChanges(): boolean {
    return this.changedIndividuals().length > 0
      || this.dirtyEntities().length > 0
      || this.dirtyKeyPeople().length > 0
      || (this.kybEntitySummary?.hasStagedReconciliationWork() ?? false);
  }

  unsavedChangeLabels(): string[] {
    const labels = this.changedIndividuals().map(info => {
      const name = `${info.individual.givenName ?? ''} ${info.individual.surname ?? ''}`.trim();
      return name || 'Individual';
    });
    if (this.dirtyEntities().length) {
      labels.push('Key Company Details');
    }
    if (this.dirtyKeyPeople().length) {
      labels.push('Beneficial Owners & Officeholders');
    }
    if (this.kybEntitySummary?.hasStagedReconciliationWork()) {
      labels.push('KYB People Reconciliation');
    }
    return labels;
  }

  // The section-level Save is enabled as soon as something actually changed. Problems with those
  // changes (invalid or duplicated fields, broken signer/guarantor rules) are reported when Save is
  // clicked instead of silently holding the button disabled with no explanation.
  disableSaveAll(): boolean {
    return !this.changedIndividuals().length
      && !this.dirtyEntities().length
      && !this.dirtyKeyPeople().length
      && !(this.kybEntitySummary?.hasStagedReconciliationWork())
      && !(this.kybEntitySummary?.stagedRemovals().length);
  }

  /**
   * Add Guarantor reloads the tab to pick up the entities and stakeholders the dialog created, and
   * that reload discards anything staged, so it waits until there is nothing staged to lose. Also
   * frozen with the rest of the party structure once the contract documents have gone out.
   */
  disableAddGuarantor(): boolean {
    return !this.canChangeParties() || !this.disableSaveAll();
  }

  addGuarantorTooltip(): string {
    if (!this.canChangeParties()) {
      return 'Parties cannot be changed once the contract documents have been issued';
    }
    return this.disableSaveAll()
      ? 'Add a guarantor'
      : 'Save your changes first - adding a guarantor reloads this tab';
  }

  // Why a Save click cannot proceed, one line per problem. Invalid individuals are also marked
  // touched (see saveAll) so their fields show mat-error text under the offending inputs.
  private saveBlockers(changed: IndividualInfo[]): string[] {
    const blockers: string[] = [];
    for (const info of changed) {
      const name = `${info.individual.givenName ?? ''} ${info.individual.surname ?? ''}`.trim() || 'Individual';
      if (info.formGroup.controls.individual.invalid) {
        blockers.push(`${name}: ${this.individualDetailProblem(info)}`);
      }
      // Additional roles have no inline editor here and are only saved when dirty (see
      // buildUpdateIndividualData), so a pre-existing invalid role must not block unrelated edits.
      if (info.formGroup.controls.additionalRoles.dirty && info.formGroup.controls.additionalRoles.invalid) {
        blockers.push(`${name}: an additional role is incomplete`);
      }
      if (this.hasDuplicateContact(info)) {
        blockers.push(`${name}: the email address or mobile number is already used by another individual on this application`);
      }
      const duplicateOf = this.duplicateIdentityName(info);
      if (duplicateOf) {
        blockers.push(`${name}: the same person is already on this application as ${duplicateOf}. Remove one of them rather than holding both.`);
      }
    }
    if (this.kybEntitySummary?.hasInvalidCompanyDetails()) {
      blockers.push('Key Company Details: the business contact email is invalid');
    }
    if (this.kybEntitySummary?.hasKeyPeopleRuleErrors()) {
      blockers.push(...this.kybEntitySummary.keyPeopleRuleErrorMessages());
    }
    const reconciliationError = this.kybEntitySummary?.reconciliationIncompleteMessage();
    if (reconciliationError) {
      blockers.push(reconciliationError);
    }
    return blockers;
  }

  // What is wrong with a person-details control known to be invalid, stated plainly: the fields are
  // no longer editable in the row, so there is nothing on screen to highlight instead.
  private individualDetailProblem(info: IndividualInfo): string {
    const missing = this.missingIndividualDetails(info.formGroup.controls.individual.value);
    const action = this.canExpand(info.individual)
      ? 'use Edit on the individual\'s menu to complete them'
      : 'click the row to complete them';
    return `missing ${missing.join(', ')} - ${action}`;
  }

  // Whether this individual's email or mobile duplicates another (non-deleted) individual's.
  private hasDuplicateContact(info: IndividualInfo): boolean {
    const v = info.formGroup.controls.individual.value;
    const email = (v?.email ?? '').trim().toLowerCase();
    const mobile = v?.mobileNumber ?? '';
    return this.allIndividualInfos()
      .filter(other => other !== info && other.individual.isDeleted !== true)
      .some(other => {
        const ov = other.formGroup.controls.individual.value;
        return (!!email && (ov?.email ?? '').trim().toLowerCase() === email)
          || (!!mobile && ov?.mobileNumber === mobile);
      });
  }

  /**
   * The name of another individual this row now duplicates, or null.
   *
   * Only runs where this edit is what turns the individual into a guarantor. These are stored
   * records, and a legacy application can already hold a duplicate pair that nobody here created;
   * checking unconditionally would block Save on both of them for any edit at all, including a
   * corrected address this screen is the only place to fix. Comparing against the stored role rather
   * than the control's dirty flag also lets a role put back to what it was save again.
   */
  private duplicateIdentityName(info: IndividualInfo): string | null {
    const signerRole = info.formGroup.controls.signerRole.value;
    const becameGuarantor = (signerRole === 'Guarantor' || signerRole === 'GuarantorSigner')
      && signerRole !== individualSignerRole(info.individual);
    if (!becameGuarantor) {
      return null;
    }
    const v = info.formGroup.controls.individual.value;
    const candidate: ComparableIdentity = {
      givenName: v?.firstName,
      middleName: v?.middleName,
      surname: v?.lastName,
      dateOfBirth: v?.dob ? v.dob.format('YYYY-MM-DD') : null,
    };
    const others: ExistingPersonRef[] = this.allIndividualInfos()
      .filter(other => other !== info && other.individual.isDeleted !== true)
      .map(other => {
        const ov = other.formGroup.controls.individual.value;
        return {
          givenName: ov?.firstName ?? other.individual.givenName,
          middleName: ov?.middleName ?? other.individual.middleName,
          surname: ov?.lastName ?? other.individual.surname,
          dateOfBirth: ov?.dob ? ov.dob.format('YYYY-MM-DD') : other.individual.dateOfBirth,
          location: `${ov?.firstName ?? other.individual.givenName ?? ''} ${ov?.lastName ?? other.individual.surname ?? ''}`.trim(),
        };
      });
    // Only an exact match blocks: there is no dialog here to host a confirm step for the softer case.
    const hit = findDuplicatePerson(candidate, others);
    return hit?.match === 'exact' ? hit.ref.location : null;
  }

  // Saved individuals plus any provisional (not yet saved) ones, for duplicate checks.
  private allIndividualInfos(): IndividualInfo[] {
    return [
      ...this.individuals,
      ...[...this.pendingNewIndividuals.values()].map(p => p.info),
    ];
  }

  // Builds the update payload for one individual from its form group (shared by the batch save).
  private buildUpdateIndividualData(individualInfo: IndividualInfo): UpdateIndividualData {
    const ind = individualInfo.individual;
    const v = individualInfo.formGroup.controls.individual.value;
    const addressValue = v?.residentialAddress ?? null;
    const parsedAddress = addressValue ? parseRawAddress(addressValue.address) : null;
    const signerRole: SignerRoleTypes = individualInfo.formGroup.controls.signerRole.value;
    // Both read straight off the control, which is the whole answer for this individual. The
    // guarantor flag used to be seeded from the stored value and only ever raised, never lowered,
    // so switching Guarantor -> Signer sent guarantor: true; the server corrected it by re-deriving
    // from the SignerRole string, which is why that string could not simply be dropped (AZ-1475).
    const guarantor = signerRole === 'Guarantor' || signerRole === 'GuarantorSigner';
    const isSigner = signerRole === 'Signer' || signerRole === 'GuarantorSigner';
    return {
      title: v?.title?.type,
      firstName: v?.firstName,
      lastName: v?.lastName,
      middleName: v?.middleName,
      birthdate: v!.dob.format('YYYY-MM-DD'),
      country: 'Australia',
      isSigner,
      email: v?.email,
      mobileNumber: v?.mobileNumber,
      rawAddress: addressValue ? addressValue.address : '',
      unitNumber: parsedAddress ? parsedAddress.unit_number ?? '' : '',
      streetNumber: parsedAddress ? parsedAddress.street_number : '',
      streetName: parsedAddress ? parsedAddress.street_name : '',
      streetType: parsedAddress ? parsedAddress.street_type ?? '' : '',
      state: parsedAddress ? parsedAddress.region ? parsedAddress.region : '' : '',
      suburb: parsedAddress ? parsedAddress.locality : '',
      postcode: parsedAddress ? parsedAddress.postal_code : '',
      guarantor: guarantor,
      ...(individualInfo.formGroup.controls.additionalRoles.dirty
        ? { additionalRoles: individualInfo.formGroup.controls.additionalRoles.getRawValue().filter((r: IndividualAdditionalRole) => r.business) }
        : {}),
      contact: individualInfo.formGroup.controls.contact.value,
      hasAlias: !!v?.hasAlias,
      aliasNames: v?.hasAlias ? (v?.aliasNames ?? null) : null,
      countryOfCitizenship: v?.countryOfCitizenship?.name ?? null,
      identificationType: v?.identification?.idType ?? null,
      identificationNumber: v?.identification?.idNumber ?? null,
    };
  }

  // Officer-decidable side effects for one individual (re-verification and/or consent reissue).
  // Empty when neither applies. Backend re-validates before acting. Re-verify is skippable for
  // internal users only. Consent issue/reissue is also skippable for brokers on pre-KYB-go-live
  // applications. Labels are person names so the dialog can group them under Individuals / Digital ID.
  private saveActionsFor(info: IndividualInfo): SaveConfirmationAction[] {
    const changedKeys = Object.keys(this.getFormControlChanges(info.individual));
    const identityChanged = changedKeys.some(key => REVERIFY_CRITICAL_CONTROLS.includes(key));
    const consentCritical = changedKeys.some(key => CONSENT_CRITICAL_CONTROLS.includes(key));

    const current = this.currentConsent(info.individual);
    const hasGatingConsent = !!current && CONSENT_REISSUE_GATING_STATUSES.includes(current.status);

    const reverifySkippable = isInternalUser(this.loggedInUser);
    const consentSkippable = canSkipPrivacyConsentSideEffects(this.loggedInUser, this.application);
    const individualId = info.individual.guid;
    const name = `${info.individual.givenName ?? ''} ${info.individual.surname ?? ''}`.trim() || 'This individual';
    const actions: SaveConfirmationAction[] = [];
    if (identityChanged && this.isStatusOk(info.individual)) {
      actions.push({ id: reverifyActionId(individualId), label: name, skippable: reverifySkippable, kind: 'reverify' });
    }
    if (consentCritical && hasGatingConsent) {
      actions.push({ id: consentReissueActionId(individualId), label: name, skippable: consentSkippable, kind: 'consentReissue' });
    }
    // First issue: a KYB/guarantor row whose form was held back, or a pre-go-live individual
    // whose details were missing at submission and are completed by this save. Unticked = skip.
    if (!current && this.completesFirstConsentIssue(info)) {
      actions.push({ id: consentReissueActionId(individualId), label: name, skippable: consentSkippable, kind: 'consentIssue' });
    }
    return actions;
  }

  /**
   * Whether this save is the first chance to issue a consent form. The server decides
   * authoritatively; this only decides whether the broker/officer is told, and given the chance
   * to hold the form back.
   */
  private completesFirstConsentIssue(info: IndividualInfo): boolean {
    if (this.missingIndividualDetails(info.formGroup.controls.individual.value).length) {
      return false;
    }
    if (isIncompleteOnArrivalSource(info.individual.source)) {
      return true;
    }
    return isApplicationSubmittedBeforeKybGoLive(this.application)
      && this.missingIndividualDetails(this.toIndividualFormInitialValue(info.individual)).length > 0;
  }

  private individualsConsequence(actions: SaveConfirmationAction[]): SaveConfirmationConsequence | null {
    const consentActions = actions.filter(action => action.kind === 'consentReissue');
    if (!consentActions.length) {
      return null;
    }
    return {
      name: 'Individuals',
      note: 'Personal details have been changed on the following individuals:',
      actions: consentActions,
    };
  }

  // The first consent form this save will send. Separate from reissue because nothing on file is
  // being invalidated. Pre-go-live individuals were submitted without these fields (and typically
  // without a DocuSign form); KYB/guarantor rows had the form held back until the record was complete.
  private deferredConsentConsequence(actions: SaveConfirmationAction[]): SaveConfirmationConsequence | null {
    const issueActions = actions.filter(action => action.kind === 'consentIssue');
    if (!issueActions.length) {
      return null;
    }
    const note = isApplicationSubmittedBeforeKybGoLive(this.application)
      ? 'These individuals now have the details required for a privacy consent form, which was not collected when the application was submitted. The form will be sent to:'
      : 'These individuals were added needing attention, so no consent form was sent at the time. '
        + 'Their details are now complete and the form will be sent to:';
    return {
      name: 'Privacy Consent',
      note,
      actions: issueActions,
    };
  }

  private digitalIdConsequence(actions: SaveConfirmationAction[]): SaveConfirmationConsequence | null {
    const reverifyActions = actions.filter(action => action.kind === 'reverify');
    if (!reverifyActions.length) {
      return null;
    }
    return {
      name: 'Digital ID',
      note: 'Identity details have been changed on the following individuals:',
      actions: reverifyActions,
    };
  }

  /**
   * The company block (annual revenue / business phone / business email / principal place of business)
   * is printed on every signatory's consent form, so one edit reissues all of them. Each affected
   * signatory gets their own skippable action so the officer can exempt individuals; anyone whose own
   * edits already trigger a reissue is excluded here so their checkbox appears only once. Signatories
   * who have already signed are called out separately because they must physically sign again.
   */
  private companyDetailsConsequence(ownConsentReissueIds: Set<string>): SaveConfirmationConsequence | null {
    if (!this.dirtyEntities().length) {
      return null;
    }

    const affected = this.individuals
      .filter(info => info.individual.isDeleted !== true && !!info.individual.guid && !ownConsentReissueIds.has(info.individual.guid))
      .map(info => ({
        id: info.individual.guid,
        name: `${info.individual.givenName ?? ''} ${info.individual.surname ?? ''}`.trim() || 'Signatory',
        consent: this.currentConsent(info.individual),
      }))
      .filter(item => !!item.consent && CONSENT_REISSUE_GATING_STATUSES.includes(item.consent.status));

    if (!affected.length) {
      return null;
    }

    const skippable = canSkipPrivacyConsentSideEffects(this.loggedInUser, this.application);
    const signed = affected.filter(item => item.consent?.status === 'accepted');
    const actions: SaveConfirmationAction[] = affected.map(item => ({
      id: consentReissueActionId(item.id),
      label: item.name,
      skippable,
      kind: 'consentReissue',
    }));
    if (signed.length) {
      actions.push({
        id: 'companyDetails:alreadySigned',
        label: `${signed.length} signatory(ies) have already signed and will need to sign again: ${signed.map(s => s.name).join(', ')}`,
        skippable: false,
      });
    }
    return {
      name: 'Commercial Entities',
      note: 'Commercial entity details have been changed; this will impact the privacy consent form of the following individuals:',
      actions,
    };
  }

  /**
   * Adding someone to the application means they have a form to sign, and removing them means an
   * unsigned form no longer applies. A guarantor/signer tick on someone who stays a party is not
   * called out because it reissues nothing; an already-signed form is left as the record either way.
   * Internal officers may opt out of sending the consent required by newly selected parties.
   */
  private keyPeopleConsequence(): SaveConfirmationConsequence | null {
    const changes = this.kybEntitySummary?.partyChanges() ?? [];
    if (!changes.length) {
      return null;
    }
    // Someone materialised from the KYB registry alone has nothing for the form to be built from and
    // arrives needing attention, so no form is offered or sent for them. Nothing is announced either:
    // the row's own flag is what asks for the details, and the form follows when they are captured.
    const added = changes.filter(change => change.becameParty && !this.stagedWithoutDetails(change.stakeholderId));
    const removed = changes.filter(change => !change.becameParty);
    const actions: SaveConfirmationAction[] = [];
    if (added.length) {
      actions.push({
        id: SELECTED_PARTY_CONSENT_ACTION_ID,
        label: `Send a privacy consent form, where required, to ${added.map(a => a.name).join(', ')}`,
        skippable: canSkipPrivacyConsentSideEffects(this.loggedInUser, this.application),
        kind: 'consentIssue',
      });
    }
    if (removed.length) {
      actions.push({
        id: 'keyPeople:void',
        label: `Any unsigned privacy consent will be voided for ${removed.map(r => r.name).join(', ')}, who are no longer parties to the application`,
        skippable: false,
      });
    }
    // Every change was a stakeholder being added needing attention, which announces nothing.
    if (!actions.length) {
      return null;
    }
    return { name: 'Beneficial Owners & Officeholders', actions };
  }

  /**
   * Changing an existing individual to guarantor while every guarantor already holds valid privacy
   * consent runs the credit flow. Internal officers may skip that run; brokers are told but cannot.
   * The role change is saved either way, and the server re-decides from the saved roles and
   * consents, so this only decides what is announced.
   */
  private guarantorCreditFlowConsequence(changed: IndividualInfo[]): SaveConfirmationConsequence | null {
    const isGuarantorRole = (role: SignerRoleTypes | undefined) => CREDIT_FLOW_GUARANTOR_ROLES.includes(role ?? null);
    const becameGuarantor = changed.filter(info =>
      info.individual.isDeleted !== true
      && !info.individual.isGuarantor
      && isGuarantorRole(info.formGroup.controls.signerRole.value));
    if (!becameGuarantor.length) {
      return null;
    }
    const guarantors = this.allIndividualInfos().filter(info =>
      info.individual.isDeleted !== true
      && isGuarantorRole(info.formGroup.controls.signerRole.value ?? individualSignerRole(info.individual)));
    const allConsentsValid = guarantors.every(info => {
      const current = this.currentConsent(info.individual);
      return (current?.status === 'accepted' && !isConsentSignatureStale(current.signedTime))
        || !!info.individual.legacyPrivacyConsent;
    });
    if (!allConsentsValid) {
      return null;
    }
    const names = becameGuarantor.map(info => `${info.individual.givenName ?? ''} ${info.individual.surname ?? ''}`.trim() || 'Individual');
    return {
      name: 'Credit Flow',
      note: `${names.join(', ')} will become a guarantor and every guarantor has valid privacy consent, so the credit flow will be run.`,
      actions: [{
        id: GUARANTOR_CREDIT_FLOW_ACTION_ID,
        label: 'Run the credit flow',
        skippable: isInternalUser(this.loggedInUser),
        kind: 'creditFlowRun',
      }],
    };
  }

  /** Announces staged entity/individual removals in the save confirmation. */
  private removalConsequence(): SaveConfirmationConsequence | null {
    const entityNames = this.kybEntitySummary?.stagedRemovalNames() ?? [];
    const individualNames = this.kybEntitySummary?.stagedIndividualRemovalNames() ?? [];
    if (!entityNames.length && !individualNames.length) {
      return null;
    }
    const actions: SaveConfirmationAction[] = [];
    if (entityNames.length) {
      actions.push({
        id: 'parties:removeEntities',
        label: `${entityNames.join(', ')} will be removed from the application`,
        skippable: false,
      });
    }
    if (individualNames.length) {
      actions.push({
        id: 'parties:removeIndividuals',
        label: `${individualNames.join(', ')} will be removed from the application`,
        skippable: false,
      });
    }
    return { name: 'Business Entities', actions };
  }

  // Section-level Save: persists all changed individuals and entities at once. Confirms once, up
  // front, listing any re-verification / consent reissue consequences when they apply.
  saveAll() {
    const changed = this.changedIndividuals();
    const entities = this.dirtyEntities();
    const keyPeople = this.dirtyKeyPeople();
    const removedParties = this.kybEntitySummary?.stagedRemovals() ?? [];

    // A selected stakeholder with no individual and nothing staged for them (e.g. a locked 100%
    // owner, selected by default and never ticked) is materialised from the KYB record here rather
    // than holding up the save. Where somebody already on the application could be that person, the
    // dialog asks which, and this save gives way to the answer.
    const missingDetails = this.selectedNewStakeholdersMissingDetails(keyPeople);
    for (const event of missingDetails) {
      if (!this.existingPersonOptionsFor(event.person).length) {
        this.stageKybPersonWithoutCandidates(event);
      }
    }
    const needsAnswer = missingDetails.find(event => this.existingPersonOptionsFor(event.person).length);
    if (needsAnswer) {
      this.onKeyPersonDetailsRequired(needsAnswer);
      return;
    }

    // Reconciliation decisions must be built from the latest provisional details and role ticks.
    // Preparing first would snapshot the stale dialog values before the expanded form is folded in.
    this.syncPendingDetailsFromForms(keyPeople);
    this.kybEntitySummary?.prepareReconciliationForSave();
    const kybReconciliations = this.dirtyReconciliations();
    if (
      !changed.length
      && !entities.length
      && !keyPeople.length
      && !removedParties.length
      && !(this.kybEntitySummary?.hasStagedReconciliationWork())
    ) {
      return;
    }

    const blockers = this.saveBlockers(changed);
    if (blockers.length) {
      this.dialogService.openAlertDialog({
        message: `Unable to save`,
        subMessage: blockers.join('<br>'),
      });
      return;
    }

    const individualActionEntries = changed.map(info => ({
      id: info.individual.guid,
      actions: this.saveActionsFor(info),
    }));
    const individualActions = individualActionEntries.flatMap(entry => entry.actions);
    // Signatories whose own edits already show a consent-reissue checkbox must not get a second,
    // identical checkbox under Commercial Entities.
    const ownConsentReissueIds = new Set(
      individualActionEntries
        .filter(entry => entry.actions.some(action => action.kind === 'consentReissue'))
        .map(entry => entry.id),
    );
    const consequences = [
      this.digitalIdConsequence(individualActions),
      this.individualsConsequence(individualActions),
      this.deferredConsentConsequence(individualActions),
      this.companyDetailsConsequence(ownConsentReissueIds),
      this.keyPeopleConsequence(),
      this.removalConsequence(),
      this.guarantorCreditFlowConsequence(changed),
    ].filter((c): c is SaveConfirmationConsequence => !!c);

    // Every checkbox shown becomes an explicit per-individual decision on the save payload:
    // left ticked = run, unticked = skip. The server records the selections in the audit log.
    const presentedActionIds = new Set(
      consequences.flatMap(c => c.actions).filter(a => a.skippable).map(a => a.id),
    );
    this.dialogService.openSaveConfirmationDialog({ consequences })
      .afterClosed().subscribe((result: SaveConfirmationDialogResult | undefined) => {
        if (result?.readyForSubmission) {
          this.doSaveAll(
            changed,
            entities,
            keyPeople,
            kybReconciliations,
            presentedActionIds,
            new Set(result.skippedActionIds ?? []),
          );
        }
      });
  }

  private doSaveAll(
    changed: IndividualInfo[],
    entities: SaveApplicationPartyEntityInput[],
    keyPeople: KybPersonRowState[],
    kybReconciliations: KybReconciliationSaveData[],
    presentedActionIds: Set<string> = new Set(),
    skippedActionIds: Set<string> = new Set(),
  ) {
    // Attach the already-synchronised staged details to stakeholders being materialised now.
    const keyPeopleWithDetails: KybPersonRowStateWithDetails[] = keyPeople.map(state => {
      const pending = this.pendingNewIndividuals.get(state.stakeholderId);
      return pending?.details && state.isSelected ? { ...state, details: pending.details } : state;
    });

    const individuals: SaveApplicationPartyIndividualInput[] = [];
    for (const info of changed) {
      if (!info.individual.guid) {
        continue;
      }
      const data = this.buildUpdateIndividualData(info);
      if (data.additionalRoles && data.additionalRoles.length > 0 && (data.email === '' || data.mobileNumber === '')) {
        this.dialogService.openAlertDialog({
          message: `Error`,
          subMessage: `Missing email or mobile number for ${info.individual.givenName ?? ''} ${info.individual.surname ?? ''}. Please enter and try again`,
        });
        return;
      }
      individuals.push({
        individualId: info.individual.guid,
        data,
        changedFields: kycChangedFieldEntries(this.getFormControlChanges(info.individual)),
      });
    }

    // The entity block always carries every field, so the edited fields (with old/new values) travel
    // separately for the entity's audit rows.
    const entityInputs: SaveApplicationPartyEntityInput[] = entities.map(entity => ({
      ...entity,
      changedFields: this.kybEntitySummary?.dirtyEntityChangedEntries(entity.commercialEntityId) ?? [],
    }));

    const removedParties = this.kybEntitySummary?.stagedRemovals() ?? [];
    if (!individuals.length && !entityInputs.length && !keyPeople.length && !removedParties.length && !kybReconciliations.length) {
      this.dialogService.openAlertDialog({ message: `Unable to save`, subMessage: `No valid changes to save` });
      return;
    }

    // Translate the presented/skipped dialog actions into per-individual run/skip decisions,
    // covering both edited individuals and company-block sweep signatories.
    const decisionsByIndividual = new Map<string, SavePartySideEffectDecision>();
    const recordDecision = (actionId: string, suffix: string, key: 'reverify' | 'consentReissue') => {
      const individualId = actionId.slice(0, -suffix.length);
      const decision = decisionsByIndividual.get(individualId) ?? { individualId };
      decision[key] = !skippedActionIds.has(actionId);
      decisionsByIndividual.set(individualId, decision);
    };
    for (const actionId of presentedActionIds) {
      if (actionId.endsWith(REVERIFY_ACTION_SUFFIX)) {
        recordDecision(actionId, REVERIFY_ACTION_SUFFIX, 'reverify');
      } else if (actionId.endsWith(CONSENT_REISSUE_ACTION_SUFFIX)) {
        recordDecision(actionId, CONSENT_REISSUE_ACTION_SUFFIX, 'consentReissue');
      }
    }
    const sideEffectDecisions = [...decisionsByIndividual.values()];
    const issueConsentForSelectedParties = presentedActionIds.has(SELECTED_PARTY_CONSENT_ACTION_ID)
      ? !skippedActionIds.has(SELECTED_PARTY_CONSENT_ACTION_ID)
      : undefined;
    const runCreditFlowOnGuarantorChange = presentedActionIds.has(GUARANTOR_CREDIT_FLOW_ACTION_ID)
      ? !skippedActionIds.has(GUARANTOR_CREDIT_FLOW_ACTION_ID)
      : undefined;

    const payload: SaveApplicationPartiesData = {
      applicationId: this.application.ApplicationId,
      applicationType: this.application.ApplicationType,
      individuals,
      ...(entityInputs.length ? { entities: entityInputs } : {}),
      ...(keyPeopleWithDetails.length ? { keyPeople: keyPeopleWithDetails } : {}),
      ...(sideEffectDecisions.length ? { sideEffectDecisions } : {}),
      ...(issueConsentForSelectedParties !== undefined ? { issueConsentForSelectedParties } : {}),
      ...(runCreditFlowOnGuarantorChange !== undefined ? { runCreditFlowOnGuarantorChange } : {}),
      ...(removedParties.length ? { removedParties } : {}),
      ...(kybReconciliations.length ? { kybReconciliations } : {}),
    };

    this.saveApplicationPartiesFn(payload).pipe(
      this.portalHotToastService.spinnerObservable(),
      this.portalHotToastService.snackBarObservable('Changes saved'),
      tap((summary: SaveApplicationPartiesSummary) => {
        const failed = summary.results.filter(r => r.consentDispatchFailed);
        if (failed.length) {
          this.dialogService.openAlertDialog({
            message: `Consent form dispatch`,
            subMessage: `Saved, but the consent form could not be dispatched for: ${failed.map(f => f.name).join(', ')}. Please check contact details and retry.`,
          });
        }
        const reconciliationFailures = (summary.kybReconciliationResults ?? [])
          .filter(result => !result.saved);
        const draftsToRestore = failedKybReconciliationDrafts(
          kybReconciliations,
          summary.kybReconciliationResults ?? [],
        );
        if (reconciliationFailures.length) {
          const succeeded = (summary.kybReconciliationResults ?? []).filter(result => result.saved).length;
          this.dialogService.openAlertDialog({
            message: succeeded ? 'Reconciliation partially saved' : 'Reconciliation not saved',
            subMessage: succeeded
              ? `${succeeded} entity reconciliation(s) saved. ${reconciliationFailures.length} failed and remain staged for correction.`
              : `${reconciliationFailures.length} entity reconciliation(s) failed and remain staged for correction.`,
          });
        }
        // Provisional rows are included: a stakeholder materialised by this save has no id on this
        // side, so their licence is filed against their name the way the legacy upload step did.
        const pendingInfos = [...this.pendingNewIndividuals.values()].map(pending => pending.info);
        this.uploadPendingIdDocumentsThenRefresh([...changed, ...pendingInfos], draftsToRestore);
      }),
    ).subscribe();
  }

  // Uploads any ID document files attached in the expanded rows, tagged the same way the legacy
  // "Upload Driver Licence" step tagged them, so they appear under the Documents tab. Refreshes
  // afterwards (refresh rebuilds the forms, which clears the in-memory files).
  private async uploadPendingIdDocumentsThenRefresh(
    infos: IndividualInfo[],
    reconciliationDrafts: KybReconciliationDraftRestore[] = [],
  ) {
    const files: File[] = [];
    for (const info of infos) {
      const v = info.formGroup.controls.individual.value;
      const name = `${v?.firstName ?? ''} ${v?.lastName ?? ''}`.trim();
      for (const f of (v?.idDocument ?? [])) {
        (f as any).tags = ['driverslicence'];
        (f as any).metadata = {
          ...this.idDocumentBaseMetadata(),
          driverslicencename: name,
          ...(info.individual.guid ? { individualid: info.individual.guid } : {}),
        };
        files.push(f);
      }
    }
    if (!files.length) {
      this.refresh(reconciliationDrafts);
      return;
    }
    (await this.uploadApplicationDocumentFn(this.application.ApplicationId, files, [])).pipe(
      this.portalHotToastService.spinnerObservable(),
      this.portalHotToastService.snackBarObservable('ID document uploaded'),
      tap(() => this.refresh(reconciliationDrafts)),
    ).subscribe();
  }

  // Same metadata shape the Documents tab attaches to uploads.
  private idDocumentBaseMetadata(): { [key: string]: string } {
    if (this.application.ApplicationType === 'Consumer') {
      const applicant = applicantOf(this.application?.parties);
      return {
        consumername: `${applicant?.givenName ?? ''} ${applicant?.surname ?? ''}`.trim(),
        dob: (applicant?.dateOfBirth as string) ?? '',
      };
    }
    const entity = primaryEntityOf(this.application?.parties);
    return entity ? { legalname: entity.legalName ?? '', abn: entity.abn ?? '' } : {};
  }

  getVerificationSessionToken(individual: IndividualWithResult): string {
    const digitalIdResult = individual.result;
    switch (digitalIdResult.status) {
      case 'RECEIVED_INCOMPLETE': {
        return digitalIdResult.digitalIdResponse?.verification_session_token ?? '';
      }
    }
    return '';
  }

  requestStatusDisplay(verificationStatus?: string): string {
    if (verificationStatus) {
      switch (verificationStatus) {
        case 'completed':
          return 'Completed';
        case 'in_progress':
          return 'In Progress';
        case 'failed':
          return 'Failed';
        case 'pending_review':
          return 'Under Review';
        default:
          return 'Not available';
      }
    }
    return 'Not available';
  }

  verificationStatusDisplay(status: any): string {
    switch (status) {
      case 'RECEIVED_COMPLETE':
        return 'Verified';
      case 'RECEIVED_FAILED':
        return 'Failed';
      case 'RECEIVED_PENDING_REVIEW':
        return 'Under Review';
      case 'RECEIVED_INCOMPLETE':
        return 'Incomplete';
      case 'RECEIVED_ERROR':
        return 'System Error';
      case 'SMS_SENT':
        return 'SMS Sent';
      case 'WAITING':
        return 'Pending';
      default:
        return status ? status.toString().replace(/_/g, ' ') : 'Not available';
    }
  }

  sourcesCategoryDisplay(sources_category: string): string {
    if (sources_category) {
      switch (sources_category) {
        case 'pep':
          return 'Pep';
        case 'sanctions':
          return 'Sanctions';
        case 'pep_and_sanctions':
          return 'Pep and Sanctions';
        case '':
          return 'Not available';
      }
    }
    return 'Not available';
  }

  capitalize(s: any): string {
    return s.toString() ? _.startCase(s.toString()) : s;
  }

  defaultString(s: any): string {
    return s ? s : 'Not available';
  }

  canActionDigitalVerification(individual: IndividualWithResult): boolean {
    const hasApplicationId = !!this.application.ApplicationId;
    const isIncomplete = individual?.result?.status === 'RECEIVED_INCOMPLETE';
    const loggedinUser = getUser();
    const isLoggedIn = !!loggedinUser;
    const hasAccess = loggedinUser?.AccessLevel === 'admin' || loggedinUser?.AccessLevel === 'internalbroker' || loggedinUser?.AccessLevel === 'salesBDM' || loggedinUser?.AccessLevel === 'salesAM';
    return (hasApplicationId && isIncomplete && isLoggedIn && hasAccess);
  }

  canUpdateKycStatus(): boolean {
    const loggedinUser = getUser();
    const isLoggedIn = !!loggedinUser;
    const hasAccess = loggedinUser?.AccessLevel === 'admin' || loggedinUser?.AccessLevel === 'operations';
    return (isLoggedIn && hasAccess);
  }

  /** A forced FrankieOne workflow is billable, matching the admin-only API authorization. */
  canRunKybWorkflow(): boolean {
    return this.loggedInUser?.AccessLevel === 'admin';
  }

  canEditAdditionalRoles(): boolean {
    if (!isInternalUser(this.loggedInUser)) {
      return false;
    }
    const status = getApplicationStatus(this.application);
    if (status === 'Approved' || status === 'Settled') {
      return this.loggedInUser?.AccessLevel === 'credit' || this.loggedInUser?.AccessLevel === 'admin';
    }
    return true;
  }

  /**
   * True once documents have been issued, freezing the party structure. Gates on the raw stage name
   * rather than getApplicationStatus, which collapses Docs Out and beyond into 'Approved'.
   */
  partyStructureLocked(): boolean {
    return isPartyStructureLocked(this.application?.AppInfoStageName);
  }

  /** Add/remove of the party structure is internal-only and frozen once documents are issued. */
  canChangeParties(): boolean {
    return isInternalUser(this.loggedInUser) && !this.partyStructureLocked();
  }


  allowDigitalIdActions(): boolean {
    const user = getUser();
    const isLoggedIn = !!user;
    const hasAccess = !!user && (user.AccessLevel === 'admin' || user.AccessLevel === 'internalbroker' || user?.AccessLevel === 'salesBDM' || user?.AccessLevel === 'salesAM');
    const status = getApplicationStatus(this.application);
    let statusOk = false;
    switch (status) {
      case 'Declined':
      case 'Draft':
      case 'Withdrawn':
        statusOk = false;
        break;
      default:
        statusOk = true;
        break;
    }
    return (isLoggedIn && hasAccess && statusOk);
  }

  disableSubmission(element: IndividualWithResult): boolean {
    // NOTE: formGroup can be valid (no validators set for it)
    //       when email / mobile have eg duplication errors
    //       where we want to disable to submission button
    return !!this.individualFormGroup(element)?.invalid || this.duplicatedEmailOrMobile(element);
  }

  duplicatedEmailOrMobile(element: IndividualWithResult): boolean {
    const info = this.findIndividualInfo(element);
    return info ? this.hasDuplicateContact(info) : false;
  }

  isTableRowExpanded(element: IndividualWithResult): boolean {
    return this.expandedElement === element && this.canExpand(element);
  }

  expandTableRow(element: IndividualWithResult) {
    // A row whose form has never been saved has no detail worth expanding into, so the click goes
    // straight to the form that completes it. Brokers also get that click-to-edit for a saved row
    // that is still missing mandatory fields (legacy individuals, no needs-attention flag).
    // Internal staff expand those - they have the Edit menu.
    if (!this.canExpand(element)) {
      this.editIndividual(element);
      return;
    }
    this.expandedElement = this.expandedElement === element ? null : element;
    
    // Download and display identity verification images if they exist
    if (this.expandedElement && element.identityVerificationResult) {
      const result = element.identityVerificationResult;
      
      // Download liveness picture
      if (result.livenessPicture && !this.livenessPicture) {
        this.downloadDocumentFromAzureFn(result.livenessPicture, 'id-verify')
          .pipe(
            tap(r => {
              // r.data is already in buffer format from API, convert to base64
              const b64 = Buffer.from(r.data as any).toString('base64');
              const mimeType = mime.getType(result.livenessPicture) ?? 'image/jpeg';
              // Store as plain string for the image component
              this.livenessPicture = `data:${mimeType};base64,${b64}`;
            })
          ).subscribe();
      }
      
      // Download driver's license
      if (result.driverLicenceFile && !this.driverLicence) {
        this.downloadDocumentFromAzureFn(result.driverLicenceFile, 'id-verify')
          .pipe(
            tap(r => {
              // r.data is already in buffer format from API, convert to base64
              const b64 = Buffer.from(r.data as any).toString('base64');
              const mimeType = mime.getType(result.driverLicenceFile) ?? 'image/jpeg';
              // Store as plain string for the image component
              this.driverLicence = `data:${mimeType};base64,${b64}`;
            })
          ).subscribe();
      }
    } else {
      // Clear images when collapsing
      this.livenessPicture = '';
      this.driverLicence = '';
    }
  }


  isStatusOk(element: IndividualWithResult) {
    return element?.result?.status == 'RECEIVED_COMPLETE';
  }

  isStatusIncomplete(element: IndividualWithResult) {
    return element?.result?.status == 'RECEIVED_INCOMPLETE';
  }

  isStatusFailed(element: IndividualWithResult) {
    return element?.result?.status == 'RECEIVED_FAILED';
  }

  isStatusError(element: IndividualWithResult) {
    return element?.result?.status == 'RECEIVED_ERROR';
  }

  isStatusWaiting(element: IndividualWithResult) {
    return element?.result?.status == 'WAITING';
  }

  isStatusSmsSent(element: IndividualWithResult) {
    return element?.result?.status == 'SMS_SENT';
  }

  isStatusPendingReview(element: IndividualWithResult) {
    return element?.result?.status == 'RECEIVED_PENDING_REVIEW';
  }

  private digitalIdResponse(element: IndividualWithResult): DigitalIdResponse | undefined {
    return (element?.result as DigitalIdResultReceived)?.digitalIdResponse;
  }

  // FrankieOne risk score and rating. Blank while verification is still in progress, when KYC was
  // never run through FrankieOne, and for individuals verified before the score was captured.
  // 0 is a real score; only null/absent values render as the dash.
  riskScore(element: IndividualWithResult): number | null {
    return parseKybRiskScore(this.digitalIdResponse(element)?.riskScore);
  }

  riskRating(element: IndividualWithResult): string | null {
    const level = this.digitalIdResponse(element)?.overallRiskLevel;
    return level ? `${level.charAt(0)}${level.slice(1).toLowerCase()}` : null;
  }

  // CSS-class token so the rating is coloured by severity (see the component SCSS).
  riskRatingClass(element: IndividualWithResult): string {
    return (this.digitalIdResponse(element)?.overallRiskLevel ?? '').toLowerCase();
  }

  // Shown once consent is signed, before any KYC record exists, so staff can start KYC from here.
  showVerificationDetails(element: IndividualWithResult): boolean {
    return !!element.result || (!!element.guid && this.isConsentAlreadySigned(element));
  }

  isVerificationResultExpanded(element: IndividualWithResult): boolean {
    if (!element.guid) return false;
    // Default is expanded, so it's only collapsed if it's in the set
    return !this.collapsedVerificationResults.has(element.guid);
  }

  toggleVerificationResult(element: IndividualWithResult) {
    if (!element.guid) return;
    if (this.collapsedVerificationResults.has(element.guid)) {
      this.collapsedVerificationResults.delete(element.guid);
    } else {
      this.collapsedVerificationResults.add(element.guid);
    }
  }

  getStatusIcon(element: IndividualWithResult): string {
    const status = element?.result?.status;
    switch (status) {
      case 'RECEIVED_COMPLETE': return 'mdi-check-circle-outline';
      case 'RECEIVED_FAILED':
      case 'RECEIVED_ERROR': return 'mdi-alert-circle-outline';
      case 'RECEIVED_INCOMPLETE': return 'mdi-progress-question';
      case 'SMS_SENT': return 'mdi-cellphone-message';
      case 'WAITING': return 'mdi-clock-outline';
      default: return 'mdi-information-outline';
    }
  }

  deleteIndividual(event: Event, element: IndividualWithResult) {
    if (element.guid) {
      if (element.isPrimaryContact) {
        this.dialogService.openAlertDialog({
          message: `Info`,
          subMessage: `Unable to delete the primary individual. Please assign another individual as primary before proceeding with deletion.`,
        });
      }
      else {
        // Deletes go through the consolidated save so the server runs its party side effects -
        // notably voiding the removed individual's unsigned privacy consent.
        this.saveApplicationPartiesFn({
          applicationId: this.application.ApplicationId,
          applicationType: this.application.ApplicationType,
          individuals: [{ individualId: element.guid, data: { deleted: true } }],
        }).pipe(
          this.portalHotToastService.spinnerObservable(),
          this.loader.deleteIndividual.track(),
          this.portalHotToastService.snackBarObservable(`Individual deleted`),
          tap(r => {
            this.refresh();
          })
        ).subscribe();
      }
    } else {
      this.dialogService.openAlertDialog({
        message: `Info`,
        subMessage: `Missing individual id, please run validation and try again`,
      });
    }
    return false;
  }

  /** Matches the merge route's access levels. */
  get canMergeIndividuals(): boolean {
    return isAdminOrCreditUser(this.loggedInUser) || isOperations(this.loggedInUser);
  }

  /**
   * Collapses several records for one real person into the one staff choose to keep. Refuses while
   * the tab holds unsaved edits because the reload that follows would discard them.
   */
  onMergeInto(element: IndividualWithResult) {
    const live = this.individuals
      .map(info => info.individual)
      .filter(individual => !!individual.guid && !!individual.id && !individual.isDeleted);
    if (this.pendingNewIndividuals.size || live.some(individual => this.hasUnsavedEdits(individual))) {
      this.dialogService.openAlertDialog({
        message: 'Unable to merge',
        subMessage: 'Save or discard your changes on this tab before merging individuals.',
      });
      return;
    }
    this.dialogService.openMergeIndividualsDialog({
      individuals: live,
      initialIndividualId: element.id,
      individualsWithIdDocument: live.filter(individual => this.idDocumentsOnFile(individual).length > 0).map(individual => individual.id),
    }).afterClosed().pipe(
      tap((result?: MergeIndividualsDialogResult) => {
        if (!result || result.cancelled || !result.survivorIndividualId || !result.mergedIndividualIds?.length) {
          return;
        }
        this.mergeIndividuals(result.survivorIndividualId, result.mergedIndividualIds);
      }),
    ).subscribe();
  }

  private mergeIndividuals(survivorIndividualId: number, mergedIndividualIds: number[]) {
    this.applicationService.mergeApplicationIndividuals({
      applicationId: this.application.ApplicationId,
      survivorIndividualId,
      mergedIndividualIds,
    }).pipe(
      this.portalHotToastService.spinnerObservable(),
      tap(result => {
        const warnings = result.warnings?.length ? ` (${result.warnings.length} follow-up step(s) failed: ${result.warnings.join(', ')})` : '';
        this.portalHotToastService.success(`Individuals merged${warnings}`, 'Merge individuals');
      }),
      this.portalHotToastService.publishErrorNotificationObservable({
        errorTitle: 'Merge individuals',
        errorMessage: 'Unable to merge the individuals',
        retryFn: () => this.mergeIndividuals(survivorIndividualId, mergedIndividualIds),
      }),
      tap(() => this.refresh()),
    ).subscribe();
  }

  onPrimaryContact(element: IndividualWithResult) {
    this.digitalIdService.updatePrimaryContact(this.application.ApplicationId, element.guid)
      .pipe(this.portalHotToastService.spinnerObservable(),
        this.portalHotToastService.snackBarObservable(`Individual updated`),
        tap(r => {
          if (r) {
            this.refresh();
          }
        })
      ).subscribe();
  }


  // Portal label for a consent record (auto-generated forms use the consent-form story wording).
  consentStatusLabel(record: PrivacyConsent): string {
    return privacyConsentDisplayStatus(record).label;
  }

  // CSS-class token for the consent status chip (kept separate from the human label).
  consentStatusStyleKey(record: PrivacyConsent): string {
    return privacyConsentDisplayStatus(record).styleKey;
  }

  // Why an admin completed this consent by hand; empty for a customer-signed record.
  consentRecordTooltip(record: PrivacyConsent): string {
    return record.bypassReason ? `Manually completed. Reason: ${record.bypassReason}` : '';
  }

  // Provenance of a consent an admin completed by hand, shown in full on the tab: this record
  // stands in for a customer signature, so who did it and why must be readable without hovering.
  // Null for a customer-signed consent. GivenName/FamilyName are the acting user, from the join.
  manuallyCompletedConsent(element: IndividualWithResult): { by: string; when: string | null; reason: string } | null {
    const current = this.currentConsent(element);
    if (!current?.bypassReason) {
      return null;
    }
    return {
      by: `${current.GivenName ?? ''} ${current.FamilyName ?? ''}`.trim() || 'an administrator',
      when: current.signedTime ?? current.createdTime ?? null,
      reason: current.bypassReason,
    };
  }

  // True when a signatory has a consent form that failed to dispatch ("Action Required").
  consentDispatchFailed(records: PrivacyConsent[] | null | undefined): boolean {
    return hasConsentDispatchFailure(records);
  }

  // The reason a signatory's consent form failed to dispatch, for the "Action Required" prompt.
  consentDispatchFailureReason(records: PrivacyConsent[] | null | undefined): string {
    return records?.find(record => record.dispatchFailed)?.failureReason ?? '';
  }

  // ── Consolidated consent status (Story 2): current record + icon/tooltip per individual ──

  // The consent record representing this individual's current state (newest non-superseded).
  currentConsent(element: IndividualWithResult): PrivacyConsent | null {
    return currentPrivacyConsent(element?.privacyConsent);
  }

  // mdi icon for the consent status shown next to the KYC status in the individuals accordion.
  consentStatusIcon(element: IndividualWithResult): string {
    const current = this.currentConsent(element);
    return current ? privacyConsentDisplayStatus(current).icon : 'mdi-help-circle-outline';
  }

  // CSS-class token so the consent icon is coloured per status (see the component SCSS).
  consentStatusIconClass(element: IndividualWithResult): string {
    const current = this.currentConsent(element);
    return current ? privacyConsentDisplayStatus(current).styleKey : 'none';
  }

  // Tooltip: current status, when it last changed, and the failure reason when dispatch failed.
  consentStatusTooltip(element: IndividualWithResult): string {
    const current = this.currentConsent(element);
    if (!current) {
      return 'No consent form generated yet';
    }
    const label = privacyConsentDisplayStatus(current).label;
    const changed = latestConsentStatusChange(current);
    const when = changed ? new Date(changed).toLocaleString('en-AU') : '';
    const detail = current.dispatchFailed ? current.failureReason : current.bypassReason;
    const reason = detail ? ` - ${detail}` : '';
    return when ? `${label} (as at ${when})${reason}` : `${label}${reason}`;
  }

  // Timestamp of a consent record's latest status change, for the expanded history table.
  consentStatusChanged(record: PrivacyConsent): string | null {
    return latestConsentStatusChange(record);
  }

  // A prior (superseded/expired) consent version shown as history in the expanded row.
  isHistoricalConsent(record: PrivacyConsent): boolean {
    return record.status === 'superseded' || record.status === 'expired';
  }

  // The individual's current consent is in a terminal state a fresh issue can resolve: the customer
  // declined, or the dispatch failed and auto-retry has been exhausted (non-retryable).
  canReissueConsent(element: IndividualWithResult): boolean {
    const current = this.currentConsent(element);
    if (!current) return false;
    return current.status === 'declined' || (!!current.dispatchFailed && !current.dispatchRetryable);
  }

  // Show the re-issue button only on the most recent (current) consent record's row.
  showReissueConsentButton(element: IndividualWithResult, record: PrivacyConsent): boolean {
    const current = this.currentConsent(element);
    return !!current && current.id === record.id && this.canReissueConsent(element);
  }

  // Label for the Privacy Consent header send/resend button.
  consentDispatchButtonLabel(element: IndividualWithResult): string {
    return element.privacyConsent?.length ? 'Resend Consent Form' : 'Send Consent Form';
  }

  // True when the individual's current consent form has already been signed (accepted).
  isConsentAlreadySigned(element: IndividualWithResult): boolean {
    return this.currentConsent(element)?.status === 'accepted';
  }

  // Disable send/resend when the individual form is invalid. A signed form stays enabled so an
  // internal user can supersede it after confirming a reason.
  disableConsentDispatch(element: IndividualWithResult): boolean {
    return this.disableSubmission(element);
  }

  // Tooltip explaining why the send/resend button is disabled (empty when enabled).
  consentDispatchDisabledTooltip(_element: IndividualWithResult): string {
    return '';
  }

  // Tooltip explaining why the KYC SMS button is disabled (empty when enabled). KYC verification
  // must not start until the individual's privacy consent form has been signed, except for
  // applications submitted before KYB go-live (ADO 1626).
  kycSmsDisabledTooltip(element: IndividualWithResult): string {
    if (!this.isConsentAlreadySigned(element) && !isApplicationSubmittedBeforeKybGoLive(this.application)) {
      return 'A signed privacy consent form is required before sending the KYC SMS';
    }
    return '';
  }

  isKycSmsDisabled(element: IndividualWithResult): boolean {
    if (!element?.guid || this.disableSubmission(element)) {
      return true;
    }
    if (isApplicationSubmittedBeforeKybGoLive(this.application)) {
      return false;
    }
    return !this.isConsentAlreadySigned(element);
  }

  // Generate + dispatch (or re-dispatch) the consent form for this individual, then refresh the view.
  // A signed form asks for a reason first; confirming supersedes it and sends a new one.
  dispatchConsentForm(element: IndividualWithResult) {
    if (!element.guid) return;
    if (this.isConsentAlreadySigned(element)) {
      const name = `${element.givenName ?? ''} ${element.surname ?? ''}`.trim() || 'This individual';
      this.dialogService.openResendSignedConsentDialog({ name }).afterClosed().subscribe((result: ResendSignedConsentDialogResult | undefined) => {
        if (!result?.readyForSubmission) return;
        this.submitConsentDispatch(
          element,
          { reissueSigned: true, reason: result.reason },
          'Signed consent superseded and new form sent',
        );
      });
      return;
    }
    const successMessage = element.privacyConsent?.length ? 'Consent form re-issued' : 'Consent form sent';
    this.submitConsentDispatch(element, undefined, successMessage);
  }

  private submitConsentDispatch(
    element: IndividualWithResult,
    options: { reissueSigned: boolean; reason: string } | undefined,
    successMessage: string,
  ) {
    if (!element.guid) return;
    this.digitalIdService.reissueConsentForm(this.application.ApplicationId, element.guid, options)
      .pipe(
        this.portalHotToastService.spinnerObservable(),
      ).subscribe((response) => {
        const result = response.payload;
        if (result?.dispatchFailed) {
          this.portalHotToastService.error(
            result.failureReason || 'Consent form could not be dispatched',
            'Consent form',
          );
        } else if (!result?.dispatched) {
          this.portalHotToastService.error(
            response.message || 'Consent cannot be reissued because the form has already been signed',
            'Consent form',
          );
        } else {
          this.portalHotToastService.snackbar(successMessage);
        }
        this.refresh();
      });
  }

  // Re-generate + re-dispatch the consent form for this individual, then refresh the view.
  reissueConsent(element: IndividualWithResult) {
    this.dispatchConsentForm(element);
  }

  // Admins can complete consent by hand when it was obtained outside DocuSign or the signing flow
  // cannot be completed. Offered only while the individual has no valid consent, so a signed (and
  // not yet stale) form is never silently replaced - that path is "Resend Consent Form".
  canBypassConsent(element: IndividualWithResult): boolean {
    if (this.loggedInUser?.AccessLevel !== 'admin' || !element?.guid) {
      return false;
    }
    const current = this.currentConsent(element);
    return !(current?.status === 'accepted' && !isConsentSignatureStale(current.signedTime));
  }

  // Record consent as complete without a customer signature, against a mandatory reason.
  bypassConsent(element: IndividualWithResult) {
    const guid = element.guid;
    if (!guid) return;
    const name = `${element.givenName ?? ''} ${element.surname ?? ''}`.trim() || 'This individual';
    this.dialogService.openResendSignedConsentDialog({
      name,
      title: 'Mark privacy consent as complete',
      warning: `Privacy consent for ${name} will be recorded as complete without a customer signature, and any outstanding consent form will be voided. The KYC verification SMS will not be sent automatically (you will be asked next) and the credit flow will not run automatically.`,
      submitLabel: 'Mark Complete',
    }).afterClosed().subscribe((result: ResendSignedConsentDialogResult | undefined) => {
      if (!result?.readyForSubmission) return;
      this.digitalIdService.bypassPrivacyConsent(this.application.ApplicationId, guid, result.reason)
        .pipe(
          this.portalHotToastService.spinnerObservable(),
          tap(response => {
            this.portalHotToastService.snackbar(response.message);
            this.refresh();
            if (response.payload?.bypassed && !this.disableSubmission(element)) {
              this.promptKycSmsAfterBypass(element, name);
            }
          }),
          this.portalHotToastService.publishErrorNotificationObservable({
            errorTitle: 'Privacy consent',
            errorMessage: 'Unable to manually complete privacy consent',
            errorSolution: 'Try again in a moment.',
            retryFn: () => this.bypassConsent(element),
          }),
        )
        .subscribe();
    });
  }

  // A bypass deliberately skips the KYC SMS a real signature sends, so offer it as an explicit step.
  private promptKycSmsAfterBypass(element: IndividualWithResult, name: string) {
    this.dialogService.openConfirmationDialog({
      message: 'Trigger KYC verification?',
      subMessage: `Privacy consent for ${name} is now complete. Send the KYC verification SMS now?`,
      confirmLabel: 'Send SMS',
      cancelLabel: 'Not now',
    }).afterClosed().subscribe((result: ConfirmationDialogResult | undefined) => {
      if (result?.readyForSubmission) {
        this._sendVerificationSmsToIndividual(element);
      }
    });
  }

  manualVerify(element: IndividualWithResult) {
    this.dialogService.openEditKycStatusDialog({ kycStatus: element?.result?.status }).afterClosed().subscribe((result: EditKycStatusDialogResult | undefined) => {
      if (result && result.readyForSubmission && result.kycStatus && result.kycStatus !== element?.result?.status ) {
        this.updateIndividualKycStatusFn(this.application.ApplicationId, element.guid, result.kycStatus, result.note)
          .pipe(this.portalHotToastService.spinnerObservable())
          .subscribe(() => {
            this.portalHotToastService.snackBarObservable(`Individual KYC status updated successfully`);
            this.refresh();
          });
      }
    });
  }

  syncKycFromFrankieOne(element: IndividualWithResult) {
    if (!element.guid || !this.syncIndividualKycFromFrankieOneFn) {
      return;
    }
    this.syncIndividualKycFromFrankieOneFn(this.application.ApplicationId, element.guid)
      .pipe(
        this.loader.syncKycFromFrankieOne.track(),
        this.portalHotToastService.spinnerObservable(),
        tap(response => {
          this.portalHotToastService.snackbar(response.message);
          if (response.payload?.updated) {
            this.refresh();
          }
        }),
        this.portalHotToastService.publishErrorNotificationObservable({
          errorTitle: 'Refresh KYC',
          errorMessage: 'Unable to refresh KYC from FrankieOne',
          errorSolution: 'Try again in a moment.',
          retryFn: () => this.syncKycFromFrankieOne(element),
        }),
      )
      .subscribe();
  }

  getDriverLicenceBoundingBoxes(element: IndividualWithResult): BoundingBoxData[] {
    return element?.identityVerificationResult?.driverLicenceBoundingBox?.map(d => d.Face) ?? [];
  }

  getLivenessPictureBoundingBoxes(element: IndividualWithResult): BoundingBoxData[] {
    return element?.identityVerificationResult?.livenessPictureBoundingBox ? [element.identityVerificationResult.livenessPictureBoundingBox] : [];
  }
}
